import axios, { AxiosInstance, AxiosError } from 'axios';
import type { CompanyOverview, CompanyOverviewPeriod } from './portalCompanyOverview';
import { useAuthStore } from '../stores/authStore';
import { scopeTicketRequest, ticketKey, ticketReference } from './ticketReference';
import { isPortalLoginPath, portalPath, PORTAL_BASE } from '../config/portalPaths';

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL && import.meta.env.VITE_API_BASE_URL.length > 0
    ? import.meta.env.VITE_API_BASE_URL
    : '/api';

const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

export const publicApiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  withCredentials: false,
  headers: { 'Content-Type': 'application/json' },
});

export const customerApiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

export const createIdempotencyKey = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  return `portal-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const pendingBookings = new Map<string, string>();
async function postTimeBooking(url: string, data: unknown, explicitKey?: string) {
  const storageKey = 'time-booking:' + String(useAuthStore.getState().user?.id ?? '') + ':' + url + ':' + JSON.stringify(data);
  let key = explicitKey || pendingBookings.get(storageKey);
  if (!key) { try { key = sessionStorage.getItem(storageKey) || undefined; } catch { /* memory fallback */ } }
  key ||= createIdempotencyKey();
  pendingBookings.set(storageKey,key);
  try { sessionStorage.setItem(storageKey,key); } catch { /* memory fallback */ }
  const response = await apiClient.post(url,data,{ headers: { 'Idempotency-Key':key } });
  pendingBookings.delete(storageKey);
  try { sessionStorage.removeItem(storageKey); } catch { /* memory fallback */ }
  return response;
}

const ticketContextConfig = (key?: string) => {
  const ref = key ? ticketReference(key) : null;
  return ref ? { headers: { 'X-Customer-Id': ref.customerId }, params: { ticket_id: ref.ticketId } } : undefined;
};

const addCsrfHeader = (requestConfig: any) => {
  const mutatingMethods = ['post', 'put', 'delete', 'patch'];
  if (requestConfig.method && mutatingMethods.includes(requestConfig.method.toLowerCase())) {
    if (requestConfig.headers && typeof requestConfig.headers.set === 'function') {
      requestConfig.headers.set('X-CSRF-Token', '1');
    } else {
      requestConfig.headers = requestConfig.headers || {};
      requestConfig.headers['X-CSRF-Token'] = '1';
    }
  }
  return requestConfig;
};

// Request deduplication for matching GET endpoints to prevent simultaneous duplicate calls (inflight sharing)
const originalGet = apiClient.get;
const getCache = new Map<string, Promise<any>>();
let authEpoch = 0;
useAuthStore.subscribe((state, previous) => {
  if (state.user !== previous.user || state.isAuthenticated !== previous.isAuthenticated || state.accessToken !== previous.accessToken) {
    authEpoch++;
    getCache.clear();
    pendingBookings.clear();
  }
});

apiClient.get = function (url: string, config?: any) {
  // Deduplicate frequently triggered, global GET requests when components mount concurrently
  const isDeduplicable = url.includes('/admin/integrations') ||
                        url.includes('/tickets/integrations/active') ||
                        url.includes('/dashboard/config') ||
                        url.includes('/settings') ||
                        url.includes('/auth/me') ||
                        url.includes('/internal-chats') ||
                        url.includes('/ai-context') ||
                        url.includes('/ai-metadata') ||
                        url.includes('/ai-predictions') ||
                        url.includes('/summary');
                        
  if (!isDeduplicable) {
    return originalGet.call(apiClient, url, config);
  }

  const cacheKey = authEpoch + ':' + url + (config ? JSON.stringify({ params: config.params || {}, headers: config.headers || {} }) : '');
  if (getCache.has(cacheKey)) {
    return getCache.get(cacheKey)!;
  }

  const promise = originalGet.call(apiClient, url, config)
    .then(res => {
      // Clear cache entry after 2 seconds to allow subsequent fresh manual updates,
      // but prevent concurrent duplicates during initial mounting/renders.
      setTimeout(() => getCache.delete(cacheKey), 2000);
      return res;
    })
    .catch(err => {
      getCache.delete(cacheKey);
      throw err;
    });

  getCache.set(cacheKey, promise);
  return promise;
} as any;

// Request interceptor - add auth token (agent/admin OR customer portal)
// Note: customerAuthStore is imported lazily to avoid circular dependency
// (customerAuthStore imports customerApi from this file)
apiClient.interceptors.request.use(
  (config) => {
    (config as any)._authEpoch = authEpoch;
    scopeTicketRequest(config);
    addCsrfHeader(config);
    const token = useAuthStore.getState().accessToken;
    if (token) {
      if (config.headers && typeof config.headers.set === 'function') {
        config.headers.set('Authorization', `Bearer ${token}`);
      } else {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

customerApiClient.interceptors.request.use(addCsrfHeader, (error) => Promise.reject(error));
// Öffentliche POST-Endpunkte (z. B. die SSO-Domainprüfung) unterliegen derselben
// CSRF-Header-Regel. Ohne diesen Interceptor erzeugte bereits das Verlassen des
// E-Mail-Feldes einen irreführenden 403 in der Browserkonsole.
publicApiClient.interceptors.request.use(addCsrfHeader, (error) => Promise.reject(error));
customerApiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      try { localStorage.removeItem('customer-auth-storage'); } catch { /* storage may be unavailable */ }
      const cleanPath = window.location.pathname.replace(/\/+$/, '').split('?')[0];
      if (!isPortalLoginPath(cleanPath)) {
        try { sessionStorage.setItem('portal-session-return', window.location.pathname + window.location.search); } catch { /* optional */ }
        window.location.href = portalPath('/login');
      }
    }
    return Promise.reject(error);
  },
);

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const config = error.config as any;
    const status = error.response?.status;

    // Only auto-retry idempotent methods. Retrying a POST/PUT/PATCH/DELETE on a 5xx
    // is unsafe: if the write already committed server-side (e.g. a user was created
    // but a follow-up step 500'd), the retry re-sends it and produces duplicates or a
    // misleading "already exists" error while the record actually exists.
    const method = ((config?.method as string) || 'get').toLowerCase();
    const isIdempotent = method === 'get' || method === 'head' || method === 'options';
    if (config?._authEpoch !== undefined && config._authEpoch !== authEpoch) return Promise.reject(error);
    if (isIdempotent && status && status >= 500 && status < 600 && config && !config._retryCount) {
      config._retryCount = (config._retryCount || 0) + 1;
      if (config._retryCount <= 2) {
        await new Promise(r => setTimeout(r, config._retryCount * 1000));
        return apiClient(config);
      }
    }

    if (status === 401) {
      // A request from an ended session cannot log out a newly authenticated user.
      if (config?._authEpoch !== authEpoch) return Promise.reject(error);
      const url = config?.url || '';
      const isPublicEndpoint = url.includes('app-settings/public') || 
        url.includes('customer-portal') || 
        url.includes('setup-status') ||
        url.includes('sso/agent-config') ||
        url.includes('public/portal-config') ||
        url.includes('public/request-types') ||
        url.includes('auth/login') ||
        url.includes('auth/mfa/verify');
      const p = window.location.pathname;
      const cleanPath = p.replace(/\/+$/, '').split('?')[0];
      const isAlreadyOnLogin = cleanPath === '/login' || isPortalLoginPath(cleanPath);
      // Customer-portal sessions use a separate store (useCustomerAuthStore) and a
      // cookie-based session. On 401 inside the portal we must clear THAT session and
      // send the user to the portal login — not the agent login.
      const isPortalContext = cleanPath === PORTAL_BASE || cleanPath.startsWith(`${PORTAL_BASE}/`);
      if (!isPublicEndpoint) {
        if (isPortalContext) {
          // Clear persisted customer session directly to avoid a circular import of
          // customerAuthStore (which imports this module).
          try { localStorage.removeItem('customer-auth-storage'); } catch { /* ignore */ }
          if (!isAlreadyOnLogin) {
            try { sessionStorage.setItem('portal-session-return', window.location.pathname + window.location.search); } catch { /* optional */ }
            window.location.href = portalPath('/login');
          }
        } else {
          useAuthStore.getState().logout();
          if (!isAlreadyOnLogin) {
            window.location.href = '/login';
          }
        }
      }
    }
    return Promise.reject(error);
  }
);

// Base API client for general requests
export const api = apiClient;
export const agentApiClient = apiClient;

// Auth API
export const authApi = {
  checkDomain: (email: string) => 
    apiClient.post('/auth/check-domain', { email }),
  
  login: (email: string, token?: string, password?: string, captchaToken?: string) => 
    apiClient.post('/auth/login', { email, token, password, captchaToken }),

  completeBootstrapAdmin: (renameToken: string, newEmail: string) =>
    apiClient.post('/auth/complete-bootstrap-admin', { renameToken, newEmail }),
  
  verifyToken: (token: string) => 
    apiClient.post('/auth/verify-token', { token }),
  
  getCurrentUser: () => 
    apiClient.get('/auth/me'),

  updateThemePreference: (themePreset: string | null, themeMode: 'light' | 'dark' | 'system' | null) =>
    apiClient.put('/auth/me/theme', { themePreset, themeMode }),
  
  logout: () => 
    apiClient.post('/auth/logout'),

  getSetupStatus: () =>
    apiClient.get('/auth/setup-status'),

  completeSetupWizard: () =>
    apiClient.post('/auth/complete-setup-wizard'),

  changePassword: (currentPassword: string, newPassword: string) =>
    apiClient.post('/auth/change-password', { currentPassword, newPassword }),

  /** Self-service password reset — request a reset email (always generic response). */
  forgotPassword: (email: string, captchaToken?: string) =>
    apiClient.post('/auth/forgot-password', { email, captchaToken }),

  /** Set a new password with the reset token; if the account has MFA, mfaCode is required. */
  resetPassword: (data: { token: string; newPassword: string; mfaCode?: string; captchaToken?: string }) =>
    apiClient.post('/auth/reset-password', data),

  setupMfa: () =>
    apiClient.post('/auth/mfa/setup'),

  enableMfa: (code: string, secret: string) =>
    apiClient.post('/auth/mfa/enable', { code, secret }),

  disableMfa: (code: string, password: string) =>
    apiClient.post('/auth/mfa/disable', { code, password }),

  verifyMfa: (code: string, mfaToken: string) =>
    apiClient.post('/auth/mfa/verify', { code, mfaToken }),

  setupEnforcedMfa: (mfaToken: string) =>
    apiClient.post('/auth/mfa/setup-enforced', { mfaToken }),

  enableEnforcedMfa: (mfaToken: string, code: string, secret: string) =>
    apiClient.post('/auth/mfa/enable-enforced', { mfaToken, code, secret }),
};

// Mobilgeräte (Android-App): Kopplung per QR, Geräteliste, Widerruf
export const mobileDevicesApi = {
  createPairingCode: () =>
    apiClient.post('/auth/mobile/pairing-code'),

  listDevices: () =>
    apiClient.get('/auth/mobile/devices'),

  revokeDevice: (id: number) =>
    apiClient.delete(`/auth/mobile/devices/${id}`),
};

// Tickets API
export const ticketsApi = {
  getAll: (params?: { customer_id?: number | string; customer_name?: string; category_name?: string; page?: number; limit?: number; status?: string; priority?: string; search?: string; sort_by?: string; sort_order?: string; assignee?: string; category?: string; source?: string; is_spam?: string; queue?: string }) =>
    apiClient.get('/tickets', { params }),
  getQueueCounts: () => apiClient.get('/tickets/queue-counts'),
  
  getById: (id: string, customerId?: string | number | null) =>
    apiClient.get(`/tickets/${ticketKey(id, customerId)}`),
  delete: (id: string) => apiClient.delete(`/tickets/${id}`),

  getTransitions: (id: string) =>
    apiClient.get(`/tickets/${id}/transitions`),
  
  create: (data: {
    title?: string;
    description?: string;
    priority?: string;
    category_id?: string;
    customer_id?: string;
    customer_employee_id?: string;
    ticket_kind?: 'incident' | 'request' | 'change_request' | 'problem';
    request_type_id?: number;
    custom_fields?: Record<string, unknown>;
    linked_asset_ids?: Array<number | string>;
    cloud_attachment_tokens?: string[];
  }, idempotencyKey?: string) => apiClient.post('/tickets', data, idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : undefined),

  requestTypesCatalog: () => apiClient.get('/tickets/request-types-catalog'),
  
  update: (id: string, data: Partial<{ title: string; description: string; status: string; priority: string; assigned_to?: string | null; category_id?: string | number | null; is_spam?: number }>) =>
    apiClient.put(`/tickets/${id}`, data),
  
  addReply: (id: string, content: string, isInternal?: boolean, mentionedAgentIds?: number[], copies?: {cc:string[];bcc:string[]}) =>
    apiClient.post<{ message: string; replyId: number }>(`/tickets/${id}/replies`, { content, is_internal: isInternal || false, mentioned_agent_ids: mentionedAgentIds, ...copies }),
  sendReplyOperation: (id:string, payload:unknown, key:string) => apiClient.post(`/tickets/${id}/reply-operations`,payload,{headers:{'Idempotency-Key':key}}),
  getReplyOperation: (id:string,operationId:string) => apiClient.get(`/tickets/${id}/reply-operations/${operationId}`),
  getReplyOperations: (id:string) => apiClient.get(`/tickets/${id}/reply-operations`),
  retryReplyOperation: (id:string,operationId:string) => apiClient.post(`/tickets/${id}/reply-operations/${operationId}/retry`),

  /** Sprachnotiz → Text (KI-Lizenz + Admin-Toggle + OpenAI-kompatibler Provider) */
  transcribeAudio: (formData: FormData) =>
    apiClient.post<{ success: boolean; text: string }>('/tickets/ai-transcribe-audio', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  getAiFeatureStatus: () =>
    apiClient.get<{
      success: boolean;
      ai_available: boolean;
      license_ai: boolean;
      features_ticket_summary: boolean;
      features_reply_suggestion: boolean;
      features_auto_categorize: boolean;
      features_sentiment_analysis: boolean;
      features_voice_notes: boolean;
      features_intake_pipeline: boolean;
      agent_voice_notes_enabled: boolean;
      voice_transcribe_supported: boolean;
      effective: {
        ticket_summary: boolean;
        reply_suggestion: boolean;
        auto_categorize: boolean;
        sentiment: boolean;
        voice_notes: boolean;
        intake_pipeline: boolean;
      };
      show_any_agent_ai: boolean;
    }>('/tickets/ai-features/status'),
  
  addTime: (id: string, minutes: number, description?: string) => 
    postTimeBooking(`/tickets/${id}/time`, { minutes, description }),

  getActivity: (id: string) =>
    apiClient.get(`/tickets/${id}/activity`),

  assign: (id: string, assigneeId?: string) =>
    apiClient.put(`/tickets/${id}`, { assigned_to: assigneeId || null }),

  reassignCustomer: (id: string, sourceCustomerId: string | number, targetCustomerId: string | number) =>
    apiClient.post(`/tickets/${id}/reassign-customer`, {
      source_customer_id: sourceCustomerId,
      target_customer_id: targetCustomerId,
    }),

  escalate: (id: string, reason?: string) =>
    apiClient.post(`/tickets/${id}/escalate`, { reason }),

  addTag: (id: string, tag: string) =>
    apiClient.post(`/tickets/${id}/tags`, { tag }),

  removeTag: (id: string, tag: string) =>
    apiClient.delete(`/tickets/${id}/tags/${encodeURIComponent(tag)}`),

  getSlaStatus: (id: string) =>
    apiClient.get(`/tickets/${id}/sla`),

  merge: (targetTicketId: string | number, sourceTicketIds: (string | number)[]) => 
    apiClient.post('/tickets/merge', { target_ticket_id: targetTicketId, source_ticket_ids: sourceTicketIds }),

  getRelations: (id: string) => apiClient.get(`/tickets/${id}/relations`),
  addRelation: (id: string, childTicketId: number, relationType?: string) =>
    apiClient.post(`/tickets/${id}/relations`, { childTicketId, relationType }),
  removeRelation: (id: string, childId: string | number) =>
    apiClient.delete(`/tickets/${id}/relations/${childId}`),

  // Tasks / Checklist
  getTasks: (ticketId: string | number) => apiClient.get(`/tickets/${ticketId}/tasks`),
  addTask: (ticketId: string | number, data: { title: string; is_mandatory?: boolean }) => apiClient.post(`/tickets/${ticketId}/tasks`, data),
  updateTask: (ticketId: string | number, taskId: string | number, data: Partial<{ title: string; is_completed: boolean; is_mandatory: boolean }>) => apiClient.put(`/tickets/${ticketId}/tasks/${taskId}`, data),
  deleteTask: (ticketId: string | number, taskId: string | number) => apiClient.delete(`/tickets/${ticketId}/tasks/${taskId}`),

  // Change Management
  getChangePlan: (id: string | number) => apiClient.get(`/tickets/${id}/change-plan`),
  saveChangePlan: (id: string | number, data: any) => apiClient.post(`/tickets/${id}/change-plan`, data),
  aiDraftChangePlan: (id: string | number) => apiClient.post(`/tickets/${id}/ai-draft-change-plan`),
  getCabVotes: (id: string | number) => apiClient.get(`/tickets/${id}/cab-votes`),
  submitCabVote: (id: string | number, vote: string, comments?: string | null) => apiClient.post(`/tickets/${id}/cab-votes`, { vote, comments }),
  getCabMembers: () => apiClient.get('/tickets/cab/members'),
  addCabMember: (data: { user_id: number | string; role: 'chair' | 'voter' }) => apiClient.post('/tickets/cab/members', data),
  removeCabMember: (userId: number | string) => apiClient.delete(`/tickets/cab/members/${userId}`),

  // Service Catalog Approvals
  getApprovals: (id: string | number) => apiClient.get(`/tickets/${id}/approvals`),
  submitApprovalDecision: (id: string | number, approvalId: string | number, status: string, comments?: string | null) =>
    apiClient.post(`/tickets/${id}/approvals/${approvalId}/decide`, { status, comments }),

  // Problem Management
  getProblems: (params?: { search?: string; known_error_only?: string | number }) =>
    apiClient.get('/tickets/problems/list', { params }),
  getProblemById: (id: string | number) => apiClient.get(`/tickets/problems/${id}`),
  createProblem: (data: {
    title: string;
    description: string;
    root_cause?: string | null;
    workaround?: string | null;
    known_error?: boolean;
    priority?: string;
  }, idempotencyKey = createIdempotencyKey()) => apiClient.post('/tickets/problems', data, { headers: { 'Idempotency-Key': idempotencyKey } }),
  updateProblem: (
    id: string | number,
    data: {
      root_cause?: string | null;
      workaround?: string | null;
      known_error?: boolean;
      problem_stage?: string;
    }
  ) => apiClient.put(`/tickets/problems/${id}`, data),
  linkIncidentsToProblem: (id: string | number, incidentIds: (number | string)[]) => apiClient.post(`/tickets/problems/${id}/link-incidents`, { incident_ids: incidentIds }),
  unlinkIncidentFromProblem: (id: string | number, incidentId: string | number) =>
    apiClient.delete(`/tickets/problems/${id}/link-incidents/${incidentId}`),
  resolveProblem: (
    id: string | number,
    data: { root_cause?: string | null; workaround?: string | null; resolution_note?: string; cascade?: boolean }
  ) => apiClient.post(`/tickets/problems/${id}/resolve`, data),
  raiseChangeFromProblem: (id: string | number, data?: { title?: string }, idempotencyKey = createIdempotencyKey()) =>
    apiClient.post(`/tickets/problems/${id}/raise-change`, data || {}, { headers: { 'Idempotency-Key': idempotencyKey } }),
  getKnownErrors: (params?: { search?: string }) =>
    apiClient.get('/tickets/problems/known-errors', { params }),
  getIncidentKnownError: (id: string | number) => apiClient.get(`/tickets/${id}/known-error`),
};

// Customers API
export const customersApi = {
  getAgentPackageUrl: (id: number | string) => `/api/admin/customers/${id}/agent-package`,
  getAll: () => 
    apiClient.get('/customers'),
  
  getById: (id: string) => 
    apiClient.get(`/customers/${id}`),
  
  search: (query: string) => 
    apiClient.get(`/customers/search?q=${encodeURIComponent(query)}`),
  
  getTickets: (id: string, status?: string) => 
    apiClient.get(`/customers/${id}/tickets`, { params: status ? { status } : {} }),

  getContracts: (id: string) =>
    apiClient.get(`/customers/${id}/contracts`),

  getEmployee: (employeeId: string) =>
    apiClient.get(`/customers/employees/${employeeId}`),

  getEmployees: (id: string, params?: { page?: number; limit?: number; search?: string }) =>
    apiClient.get(`/customers/${id}/employees`, { params }),
  
  create: (data: { 
    company_name: string; 
    email: string; 
    contact_person?: string;
    phone?: string;
    managing_director?: string;
  }) => 
    apiClient.post('/customers', data),
  
  update: (id: string, data: Partial<{
    company_name: string;
    managing_director?: string;
    contact_person?: string;
    email: string;
    phone?: string;
    mobile?: string;
    street?: string;
    zip_code?: string;
    city?: string;
    country?: string;
    tax_number?: string;
    vat_id?: string;
    sla_tier_id?: number;
    notes?: string;
    is_active?: number;
  }>) =>
    apiClient.put(`/customers/${id}`, data),
  
  delete: (id: string) => 
    apiClient.delete(`/customers/${id}`),

  impersonateUser: (params: { userId?: string | number; email?: string }) =>
    apiClient.post('/customers/impersonate-user', params),
};

// SLA API
export const slaApi = {
  getAll: () => 
    apiClient.get('/sla'),
  
  create: (data: {
    name: string;
    description?: string;
    response_time_minutes: number;
    resolution_time_minutes: number;
    priority?: string;
  }) => 
    apiClient.post('/sla', data),
  
  update: (id: string, data: Partial<{
    name: string;
    description: string;
    response_time_minutes: number;
    resolution_time_minutes: number;
    priority: string;
    is_active: number;
  }>) => 
    apiClient.put(`/sla/${id}`, data),
  
  delete: (id: string) => 
    apiClient.delete(`/sla/${id}`),
};

// Settings API
export const settingsApi = {
  getAll: () => 
    apiClient.get('/settings'),
  
  getPublic: () => 
    apiClient.get('/settings/public'),
  
  update: (key: string, value: string) => 
    apiClient.put(`/settings/${key}`, { value }),
  
  updateMultiple: (settings: Record<string, string>) => 
    apiClient.put('/settings', { settings }),

  updateTicketIdFormat: (format: string) =>
    apiClient.put('/settings/ticket-id-format', { format }),
};

// ─── Software Inventory (enterprise) types ───
export type SoftwareLicenseStatus = 'required' | 'free' | 'unassigned';
export type CveSeverity = 'critical' | 'high' | 'medium' | 'low' | 'none';

export interface SoftwareProduct {
  name: string;
  publisher: string;
  asset_count: number;
  install_count: number;
  version_count: number;
  license_status: SoftwareLicenseStatus;
  license_type: string | null;
  lifecycle_status: string | null;
  is_eol: boolean;
  is_eos: boolean;
  eol_date: string | null;
  eos_date: string | null;
  last_seen: string | null;
  cve_count: number;
  max_cvss: number;
  cve_severity: CveSeverity;
}

export interface SoftwareInventorySummary {
  total_products: number;
  total_installs: number;
  total_devices: number;
  license_required: number;
  free: number;
  unassigned: number;
  eol_count: number;
  vulnerable_products: number;
  publisher_count: number;
}

export interface SoftwareInventoryResponse {
  success: boolean;
  software: SoftwareProduct[];
  total: number;
  page: number;
  pageSize: number;
  summary: SoftwareInventorySummary;
  facets: {
    publishers: Array<{ name: string; product_count: number }>;
    statuses: { required: number; free: number; unassigned: number };
  };
}

export interface SoftwareInventoryParams {
  customer_id?: number;
  search?: string;
  publisher?: string;
  license_status?: SoftwareLicenseStatus;
  eol?: '1';
  vulnerable?: '1';
  sort?: 'devices' | 'installs' | 'versions' | 'name' | 'last_seen' | 'cve';
  order?: 'asc' | 'desc';
  page?: number;
  pageSize?: number;
}

export interface SoftwareInstallation {
  asset_id: number;
  asset_name: string;
  asset_number: string;
  version: string | null;
  publisher: string | null;
  install_date: string | null;
  last_used_date: string | null;
  agent_last_scan: string | null;
  assigned_employee_id: number | null;
  location: string | null;
  status: string | null;
  customer_id: number;
}

export interface AssetVulnerability {
  id: number;
  product_name: string;
  version: string | null;
  cve_id: string;
  severity: string | null;
  cvss_score: number | null;
  description: string | null;
  published_date: string | null;
  reference_url: string | null;
  scanned_at: string | null;
}

// Assets API
export const assetsApi = {
  getAll: (params?: { customer_id?: string; search?: string; employee_id?: string; category_id?: string; status?: string }) => 
    apiClient.get('/assets', { params }),
  
  getById: (id: string, customerId?: number | null) =>
    apiClient.get(`/assets/${id}`, { params: customerId ? { customer_id: customerId } : undefined }),

  create: (data: {
    name: string;
    asset_number?: string;
    type?: string;
    status?: string;
    customer_id?: number;
    description?: string;
    purchase_date?: string;
    warranty_end?: string;
    category_id?: number;
    assigned_employee_id?: number;
    serial_number?: string;
    manufacturer?: string;
    model?: string;
    location?: string;
    ip_address?: string;
    mac_address?: string;
    notes?: string;
    zabbix_host_id?: string;
    uptime_monitor_id?: string;
    remote_id?: string;
  }) => 
    apiClient.post('/assets', data),
  
  update: (id: string, data: Record<string, any>, customerId?:number|null) =>
    apiClient.put(`/assets/${id}`, data, {params:{customer_id:customerId}}),

  delete: (id: string, customerId?:number|null) =>
    apiClient.delete(`/assets/${id}`, {params:{customer_id:customerId}}),
  
  getTicketAssets: (ticketId: string) => 
    apiClient.get(`/assets/ticket/${ticketId}`),
  
  linkToTicket: (ticketId: string, assetId: string) => 
    apiClient.post('/assets/link', { ticket_id: ticketId, asset_id: assetId }),
  
  unlinkFromTicket: (ticketId: string, assetId: string) => 
    apiClient.post('/assets/unlink', { ticket_id: ticketId, asset_id: assetId }),

  getAuditLedger: (id: string | number, customerId?: number | null) =>
    apiClient.get(`/assets/${id}/audit-ledger`, { params: customerId ? { customer_id: customerId } : undefined }),

  /** CVE findings for a single asset (from the latest CVE scan). */
  getVulnerabilities: (id: string | number, customerId?: number | null) =>
    apiClient.get<{ success: boolean; vulnerabilities: AssetVulnerability[] }>(`/assets/${id}/vulnerabilities`, { params: customerId ? { customer_id: customerId } : undefined }),

  /** Data lineage — which discovery sources (agent/connector/…) fed this asset. */
  getSources: (id: string | number, customerId?: number | null) =>
    apiClient.get<{ success: boolean; sources: any[] }>(`/assets/${id}/sources`, { params: customerId ? { customer_id: customerId } : undefined }),

  /** Unlink a source + stop auto-merging this asset. Admin-gated. */
  unlinkSource: (id: string | number, sourceId: number, customerId?: number | null) =>
    apiClient.delete<{ success: boolean; message: string }>(`/assets/${id}/sources/${sourceId}`, { params: customerId ? { customer_id: customerId } : undefined }),

  // Asset AI (respond { available:false } when the toggle/AI provider is off)
  aiEstateSummary: (customerId: number) =>
    apiClient.get<{ available: boolean; summary?: string; stats?: any }>(`/assets/ai/estate-summary`, { params: { customer_id: customerId } }),
  aiClassify: (id: string | number, customerId?:number|null) =>
    apiClient.post<{ available: boolean; classification?: any }>(`/assets/${id}/ai-classify`, {}, {params:{customer_id:customerId}}),
  aiNormalizeSoftware: (assetId: number) =>
    apiClient.post<{ available: boolean; items?: any[] }>(`/assets/ai/normalize-software`, { asset_id: assetId }),

  /** Per-asset scan snapshot timeline (newest first, up to 30). Admin-gated. */
  getScanHistory: (id: string | number, customerId?: number | null) =>
    apiClient.get(`/assets/${id}/scan-history`, { params: customerId ? { customer_id: customerId } : undefined }),

  /** Full JSON payload of a single scan snapshot (JSON fields are strings, may be null). Admin-gated. */
  getScanSnapshot: (id: string | number, snapshotId: string | number, customerId?: number | null) =>
    apiClient.get(`/assets/${id}/scan-snapshot/${snapshotId}`, { params: customerId ? { customer_id: customerId } : undefined }),

  /** Fleet-wide software inventory, aggregated by product name (enriched: license, lifecycle/EOL, CVE). */
  getSoftwareInventory: (params: SoftwareInventoryParams = {}) =>
    apiClient.get<SoftwareInventoryResponse>('/assets/software', { params }),

  /** Which machines have a given product installed. */
  getSoftwareInstallations: (name: string, customerId?: number) =>
    apiClient.get<{ success: boolean; installations: SoftwareInstallation[] }>('/assets/software/installations', { params: { name, customer_id: customerId } }),

  // Categories
  getCategories: (customerId?:number|string|null) =>
    apiClient.get('/assets/categories', {params:{customer_id:customerId||undefined}}),

  createCategory: (data: { name: string; icon?: string; color?: string; sort_order?: number }, customerId?:number|string|null) =>
    apiClient.post('/assets/categories', data, {params:{customer_id:customerId||undefined}}),

  updateCategory: (id: number, data: { name?: string; icon?: string; color?: string; sort_order?: number }, customerId?:number|string|null) =>
    apiClient.put(`/assets/categories/${id}`, data, {params:{customer_id:customerId||undefined}}),

  deleteCategory: (id: number, customerId?:number|string|null) =>
    apiClient.delete(`/assets/categories/${id}`, {params:{customer_id:customerId||undefined}}),

  /** UTF-8 CSV mit Kopfzeile + Beispielzeile (Admin) */
  downloadImportTemplate: () =>
    apiClient.get('/assets/import/template', { responseType: 'blob' }),

  /** multipart field name: file */
  importCsv: (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return apiClient.post('/assets/import/csv', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  getCommands: (uuid: string) =>
    apiClient.get(`/assets/${uuid}/commands`),

  queueCommand: (uuid: string, command: string, ticketId?: string | number | null, isAutoRepair?: boolean) =>
    apiClient.post(`/assets/${uuid}/commands`, { command, ticket_id: ticketId, is_auto_repair: isAutoRepair }),

  getScannerSettings: (customerId: number) =>
    apiClient.get(`/assets/scanner/settings/${customerId}`),

  updateScannerSettings: (customerId: number, data: any) =>
    apiClient.put(`/assets/scanner/settings/${customerId}`, data),

  getDiscoveredDevices: (params?: { status?: string; customer_id?: number }) =>
    apiClient.get('/assets/scanner/discovery', { params }),

  ignoreDevice: (id: number, customerId?: number | null) =>
    apiClient.post(`/assets/scanner/discovery/${id}/ignore`, undefined, { params: customerId ? { customer_id: customerId } : undefined }),

  importDevice: (id: number, data: { name: string; categoryId: number; customer_id?: number | null }) =>
    apiClient.post(`/assets/scanner/discovery/${id}/import`, data),

  getRelationships: (assetId: string, customerId?: number | null) =>
    apiClient.get(`/assets/${assetId}/relationships`, { params: customerId ? { customer_id: customerId } : undefined }),

  /** Auto-inferred + manual dependencies for an asset, plus the reverse (impact). */
  getDependencies: (assetId: string | number, customerId?: number | null) =>
    apiClient.get<{ success: boolean; depends_on: any[]; dependents: any[] }>(`/assets/${assetId}/dependencies`, { params: customerId ? { customer_id: customerId } : undefined }),

  /** (Re)infer the dependency graph (VM→host, AD/DNS) for a customer (or all). */
  recomputeDependencies: (customerId?: number) =>
    apiClient.post<{ success: boolean; edges?: number; domain_controllers?: number; dns_servers?: number }>('/assets/dependencies/recompute', customerId ? { customer_id: customerId } : {}),

  getMonitoring: (id: string | number, customerId?: number | null) =>
    apiClient.get(`/assets/${id}/monitoring`, { params: customerId ? { customer_id: customerId } : undefined }),

  createRelationship: (data: { source_asset_id: number; target_asset_id: number; relationship_type: string; customer_id?: number | null }) =>
    apiClient.post('/assets/relationships', data),

  deleteRelationship: (relationshipId: number, customerId?: number | null) =>
    apiClient.delete(`/assets/relationships/${relationshipId}`, { params: customerId ? { customer_id: customerId } : undefined }),

  getPendingCommandApprovals: () =>
    apiClient.get('/admin/commands/approvals'),

  approveCommand: (id: number) =>
    apiClient.post(`/admin/commands/approvals/${id}/approve`),

  rejectCommand: (id: number) =>
    apiClient.post(`/admin/commands/approvals/${id}/reject`),
};

// Licenses API
export const licensesApi = {
  getAudit: (customerId: number) =>
    apiClient.get(`/admin/licenses/customer/${customerId}`),

  getHistory: (customerId: number) =>
    apiClient.get(`/admin/licenses/customer/${customerId}/history`),

  create: (data: {
    customer_id: number;
    name: string;
    license_type: 'windows_server_core' | 'sql_server_core' | 'user_cal' | 'device_cal' | 'seat_per_installation' | 'socket_based' | 'concurrent_user';
    purchased_quantity: number;
    unit_price?: number;
    license_key?: string;
    agreement_number?: string;
    has_software_assurance?: boolean;
    notes?: string;
    match_patterns?: string[];
    product_family?: string;
    license_version?: string;
    downgrade_rights?: boolean | number;
    upgrade_rights?: boolean | number;
    maintenance_start_date?: string;
    maintenance_end_date?: string;
    license_key_type?: 'volume' | 'retail' | 'oem';
    contract_id?: number | null;
  }) =>
    apiClient.post('/admin/licenses', data),

  update: (id: number, data: Partial<{
    name: string;
    purchased_quantity: number;
    unit_price: number;
    license_key: string;
    agreement_number: string;
    has_software_assurance: boolean;
    notes: string;
    match_patterns: string[];
    product_family: string;
    license_version: string;
    downgrade_rights: boolean | number;
    upgrade_rights: boolean | number;
    maintenance_start_date: string;
    maintenance_end_date: string;
    license_key_type: 'volume' | 'retail' | 'oem';
    contract_id: number | null;
  }>) =>
    apiClient.put(`/admin/licenses/${id}`, data),

  delete: (id: number) =>
    apiClient.delete(`/admin/licenses/${id}`),

  getSettings: (customerId: number) =>
    apiClient.get(`/admin/licenses/customer/${customerId}/settings`),

  saveSettings: (customerId: number, settings: any) =>
    apiClient.post(`/admin/licenses/customer/${customerId}/settings`, { settings }),

  getSealed: (customerId: number) =>
    apiClient.get(`/admin/licenses/customer/${customerId}/sealed`),

  sealAudit: (customerId: number, notes: string) =>
    apiClient.post(`/admin/licenses/customer/${customerId}/sealed`, { notes }),

  bulkClassifySoftware: (customerId: number, softwareNames: string[], isLicenseRequired: boolean, licenseType?: string) =>
    apiClient.post(`/admin/licenses/customer/${customerId}/software-catalog/bulk-classify`, { softwareNames, isLicenseRequired, licenseType }),

  bulkUninstallSoftware: (customerId: number, reclaims: { assetId: number, softwareName: string }[]) =>
    apiClient.post(`/admin/licenses/customer/${customerId}/reclaims/bulk-uninstall`, { reclaims }),

  getContracts: (customerId: number) =>
    apiClient.get(`/admin/licenses/customer/${customerId}/contracts`),

  createContract: (data: any) =>
    apiClient.post('/admin/licenses/contracts', data),

  updateContract: (id: number, data: any) =>
    apiClient.put(`/admin/licenses/contracts/${id}`, data),

  deleteContract: (id: number) =>
    apiClient.delete(`/admin/licenses/contracts/${id}`),

  reconcile: (customerId: number) =>
    apiClient.post(`/admin/licenses/customer/${customerId}/reconcile`),

  createPurchaseRequest: (payload: { customer_id: number; software_model: string; units?: number; cost?: number; product_family?: string }, idempotencyKey: string) =>
    apiClient.post<{ success: boolean; ticket_number: string; ticket_id: number; message: string }>('/admin/licenses/purchase-request', payload, { headers: { 'Idempotency-Key': idempotencyKey } }),

  getSuiteComponents: (customerId: number) =>
    apiClient.get(`/admin/licenses/customer/${customerId}/suites`),

  createSuiteComponent: (data: { customer_id: number; suite_name: string; component_name: string }) =>
    apiClient.post('/admin/licenses/suites', data),

  deleteSuiteComponent: (id: number) =>
    apiClient.delete(`/admin/licenses/suites/${id}`),
};

// Virtualization Connectors & Topology API
export const absenceApi = {
  list: (params?: { user_id?: number; status?: string; type?: string; from?: string; to?: string }) =>
    apiClient.get<{ absences: any[] }>('/dashboard/absences', { params: params && Object.keys(params).length ? params : undefined }),
  agents: () => apiClient.get<{ agents: { id: number; name: string; email: string }[] }>('/dashboard/absence-agents'),
  create: (data: { user_id?: number; absence_type: string; start_date: string; end_date: string; half_day?: boolean; substitute_user_id?: number; note?: string }) =>
    apiClient.post<{ success: boolean; id: number; status: string; working_days: number }>('/dashboard/absences', data),
  decide: (id: number, data: { status: 'approved' | 'rejected'; decision_note?: string }) =>
    apiClient.patch<{ success: boolean; status: string }>(`/dashboard/absences/${id}`, data),
  remove: (id: number) => apiClient.delete(`/dashboard/absences/${id}`),
};

export const virtualizationApi = {
  getConnectors: (customerId?: number) =>
    apiClient.get<{ success: boolean; connectors: any[] }>('/admin/virtualization/connectors', {
      params: customerId ? { customer_id: customerId } : undefined
    }),

  getConnectorActivity: (params?: { customer_id?: number; connector_id?: number; type?: string; q?: string; limit?: number }) =>
    apiClient.get<{ success: boolean; activity: any[] }>('/admin/virtualization/connectors/activity', {
      params: params && Object.keys(params).length ? params : undefined
    }),

  exportConnectorActivity: (params?: { customer_id?: number; connector_id?: number; type?: string; q?: string; limit?: number }) =>
    apiClient.get('/admin/virtualization/connectors/activity', {
      params: { ...(params || {}), format: 'csv' },
      responseType: 'blob'
    }),

  scanAsset: (data: { asset_id: number; customer_id?: number; scan_types?: string[] }) =>
    apiClient.post<{ success: boolean; queued: boolean; message: string }>('/admin/virtualization/connectors/scan-asset', data),

  /** Push the client scanner (agent) onto an asset via the infrastructure connector. */
  deployAgent: (assetId: number, customerId?: number) =>
    apiClient.post<{ success: boolean; queued: boolean; message: string }>('/admin/virtualization/connectors/deploy-agent', { asset_id: assetId, customer_id: customerId }),

  /** Push the current agent onto a host that already runs an outdated one (in-place upgrade). */
  updateAgent: (assetId: number, customerId?: number) =>
    apiClient.post<{ success: boolean; queued: boolean; message: string; from_version?: string; target_version?: string }>(
      '/admin/virtualization/connectors/update-agent', { asset_id: assetId, customer_id: customerId }),

  /** Tell an outdated connector to update itself (signed MSI, msiexec in-place). */
  selfUpdateConnector: (connectorId: number, customerId?: number) =>
    apiClient.post<{ success: boolean; queued: boolean; message: string; from_version?: string; target_version?: string }>(
      `/admin/virtualization/connectors/${connectorId}/self-update`, { customer_id: customerId }),

  aiDiagnose: (log: string[]) =>
    apiClient.post<{ available: boolean; diagnosis?: string }>('/admin/virtualization/connectors/ai-diagnose', { log }),

  createConnector: (data: { name: string; network_profile?: 'LAN' | 'MPLS' | 'WAN'; customer_id?: number; replace?: boolean }) =>
    apiClient.post<{ success: boolean; message: string; connectorId: number; activation_token: string }>('/admin/virtualization/connectors', data),

  updateConnector: (id: number, data: Partial<{ name: string; network_profile: 'LAN' | 'MPLS' | 'WAN'; conflict_priority_rules_json: string; default_auth_username: string; default_auth_secret: string; settings_json: string; customer_id?: number }>) =>
    apiClient.put(`/admin/virtualization/connectors/${id}`, data),

  deleteConnector: (id: number, customerId?: number) =>
    apiClient.delete(`/admin/virtualization/connectors/${id}`, {
      params: customerId ? { customer_id: customerId } : undefined
    }),

  testConnector: (id: number, customerId?: number) =>
    apiClient.post<{ success: boolean; online: boolean; status: string; last_heartbeat: string | null; target_count: number; message: string }>(`/admin/virtualization/connectors/${id}/test`, {}, {
      params: customerId ? { customer_id: customerId } : undefined
    }),

  syncConnector: (id: number, customerId?: number) =>
    apiClient.post<{ success: boolean; message: string }>(`/admin/virtualization/connectors/${id}/sync`, {}, {
      params: customerId ? { customer_id: customerId } : undefined
    }),

  getTargets: (connectorId: number, customerId?: number) =>
    apiClient.get<{ success: boolean; targets: any[] }>(`/admin/virtualization/connectors/${connectorId}/targets`, {
      params: customerId ? { customer_id: customerId } : undefined
    }),

  createTarget: (connectorId: number, data: { 
    target_name: string; 
    ip_range_or_dns: string; 
    auth_type: string; 
    auth_username?: string; 
    auth_secret?: string; 
    priority_order?: number; 
    search_filter?: string; 
    scan_types_json?: string | string[];
    scan_schedule_cron?: string;
    transfer_schedule_cron?: string;
    customer_id?: number;
  }) =>
    apiClient.post<{ success: boolean; targetId: number }>(`/admin/virtualization/connectors/${connectorId}/targets`, data),

  updateTarget: (targetId: number, data: {
    target_name: string; ip_range_or_dns: string; auth_type: string;
    auth_username?: string; auth_secret?: string; priority_order?: number;
    search_filter?: string; scan_types_json?: string | string[];
    scan_schedule_cron?: string; transfer_schedule_cron?: string; customer_id?: number;
  }) =>
    apiClient.put(`/admin/virtualization/connectors/targets/${targetId}`, data),

  deleteTarget: (targetId: number, customerId?: number) =>
    apiClient.delete(`/admin/virtualization/connectors/targets/${targetId}`, {
      params: customerId ? { customer_id: customerId } : undefined
    }),

  getPolicies: (customerId?: number) =>
    apiClient.get<{ success: boolean; policies: any[] }>('/admin/virtualization/policies', {
      params: customerId ? { customer_id: customerId } : undefined
    }),

  createPolicy: (data: { policy_name: string; target_query: string; software_package_url?: string; desired_registry_state?: string; customer_id?: number }) =>
    apiClient.post<{ success: boolean; policyId: number }>('/admin/virtualization/policies', data),

  deletePolicy: (id: number, customerId?: number) =>
    apiClient.delete(`/admin/virtualization/policies/${id}`, {
      params: customerId ? { customer_id: customerId } : undefined
    }),

  getTopology: (customerId?: number) =>
    apiClient.get<{ success: boolean; topology: any[] }>('/admin/cmdb/topology', {
      params: customerId ? { customer_id: customerId } : undefined
    }),

  pushAgent: (targetId: number, data: { username: string; password?: string; customer_id?: number }) =>
    apiClient.post<{ success: boolean; message: string }>(`/admin/virtualization/connectors/targets/${targetId}/push-agent`, data)
};

export const cybersecurityApi = {
  getAudit: () =>
    apiClient.get<{ success: boolean; secure_score: number; vulnerabilities: any[]; policies: any; cve_enabled?: boolean; cve_last_scan?: string | null; cve_scanning?: boolean }>('/admin/cybersecurity/audit'),
  updatePolicies: (data: any) =>
    apiClient.post('/admin/cybersecurity/policies', data),
  triggerCveScan: () =>
    apiClient.post<{ success: boolean; running: boolean; message: string }>('/admin/cybersecurity/cve-scan', {})
};

export const aiopsApi = {
  correlate: () =>
    apiClient.get<{ success: boolean; alerts: any[] }>('/admin/aiops/correlate'),
  diagnoseTicket: (ticketId: number | string) =>
    apiClient.get<{ success: boolean; asset_name: string; agent_uuid: string | null; diagnostics: any; license_alerts: any[] }>(`/admin/tickets/${ticketId}/diagnose`)
};

// Email Templates API
export const emailTemplatesApi = {
  getAll: () => 
    apiClient.get('/email-templates'),
  
  getByEvent: (eventType: string) => 
    apiClient.get(`/email-templates/event/${eventType}`),
  
  create: (data: {
    name: string;
    subject: string;
    body_html: string;
    body_text?: string;
    event_type: string;
    variables?: string[];
  }) => 
    apiClient.post('/email-templates', data),
  
  update: (id: string, data: Partial<{
    name: string;
    subject: string;
    body_html: string;
    body_text: string;
    event_type: string;
    variables: string[];
    is_active: number;
  }>) => 
    apiClient.put(`/email-templates/${id}`, data),
  
  delete: (id: string) => 
    apiClient.delete(`/email-templates/${id}`),
};

// Mailbox API
export const mailboxApi = {
  reprocess: (id: number, externalMessageId: string) => apiClient.post(`/mailbox/${id}/reprocess`, { external_message_id: externalMessageId }),
  getAll: () => 
    apiClient.get('/mailbox'),
  
  create: (data: Record<string, any>) => 
    apiClient.post('/mailbox', data),
  
  update: (id: string | number, data: Record<string, any>) => 
    apiClient.put(`/mailbox/${id}`, data),
  
  delete: (id: string | number) => 
    apiClient.delete(`/mailbox/${id}`),
  
  test: (id: string | number) => 
    apiClient.post(`/mailbox/${id}/test`),

  run: (id: string | number) =>
    apiClient.post(`/mailbox/${id}/run`),

  getStatus: (id: string | number) =>
    apiClient.get(`/mailbox/${id}/status`),
  
  startMonitoring: () => 
    apiClient.post('/mailbox/monitoring/start'),
  
  stopMonitoring: () => 
    apiClient.post('/mailbox/monitoring/stop'),
  
  getMonitoringStatus: () => 
    apiClient.get('/mailbox/monitoring/status'),

  getFilters: (mailboxId: string | number) =>
    apiClient.get(`/mailbox/${mailboxId}/filters`),

  createFilter: (mailboxId: string | number, data: Record<string, any>) =>
    apiClient.post(`/mailbox/${mailboxId}/filters`, data),

  updateFilter: (id: string | number, data: Record<string, any>) =>
    apiClient.put(`/mailbox/filters/${id}`, data),

  deleteFilter: (id: string | number) =>
    apiClient.delete(`/mailbox/filters/${id}`),

  getRecipientRoutes: (mailboxId: string | number) =>
    apiClient.get(`/mailbox/${mailboxId}/recipient-routes`),

  createRecipientRoute: (mailboxId: string | number, data: Record<string, any>) =>
    apiClient.post(`/mailbox/${mailboxId}/recipient-routes`, data),

  updateRecipientRoute: (id: string | number, data: Record<string, any>) =>
    apiClient.put(`/mailbox/recipient-routes/${id}`, data),

  getRecipientRouteOptions: (mailboxId: string | number) =>
    apiClient.get(`/mailbox/${mailboxId}/recipient-route-options`),

  testRecipientRoute: (mailboxId: string | number, data: Record<string, any>) =>
    apiClient.post(`/mailbox/${mailboxId}/recipient-routes/test`, data),

  deleteRecipientRoute: (id: string | number) =>
    apiClient.delete(`/mailbox/recipient-routes/${id}`),
};

// Mail API (SMTP Settings)
export const mailApi = {
  getSettings: () => 
    apiClient.get('/mail/settings'),
  
  updateSettings: (data: {
    host?: string;
    port?: number;
    encryption?: 'none' | 'tls' | 'starttls';
    username?: string;
    password?: string;
    from_address?: string;
    reply_to_address?: string;
    is_active?: boolean;
    send_via?: 'smtp' | 'graph' | 'brevo';
    send_via_mailbox_id?: number | null;
    brevo_api_key?: string;
    brevo_from_name?: string;
  }) => 
    apiClient.put('/mail/settings', data),
  
  testSMTP: (data: {
    host: string;
    port: number;
    encryption?: string;
    username?: string;
    password?: string;
  }) => 
    apiClient.post('/mail/settings/test-smtp', data),
  
  sendTestEmail: (toAddress: string) => 
    apiClient.post('/mail/settings/test-send', { to_address: toAddress }),
  
  getAuditLog: (params?: { ticket_id?: string; limit?: number }) => 
    apiClient.get('/mail/audit-log', { params }),
  
  getMailboxesStatus: () => 
    apiClient.get('/mail/mailboxes'),
};

// Time Tracking API
export const timeTrackingApi = {
  getSettings: (ticket?: string) =>
    apiClient.get('/time-tracking/settings', ticketContextConfig(ticket)),
  
  updateSettings: (data: {
    enabled?: boolean;
    default_rounding_minutes?: number;
    time_billable_by_default?: boolean;
    require_time_on_close?: boolean;
    allow_timer?: boolean;
  }) => 
    apiClient.put('/time-tracking/settings', data),
  
  getEntries: (params?: { 
    ticket_id?: string; 
    agent_id?: string; 
    from_date?: string; 
    to_date?: string;
    billable_only?: boolean;
  }) => 
    apiClient.get('/time-tracking/entries', { params }),
  
  getTicketEntries: (ticketId: string) => 
    apiClient.get(`/time-tracking/ticket/${ticketId}`),
  
  addEntry: (ticketId: string, data: {
    minutes: number;
    description: string;
    work_date: string;
    is_billable?: boolean;
    outside_service_hours?: boolean;
    activity_kind?:string;contract_id?:number;component_key?:string;work_started_at?:string;work_ended_at?:string;cost_approval_id?:number;timer_result_id?:number;visit_key?:string;travel_leg?:string;
  }) => 
    postTimeBooking(`/time-tracking/ticket/${ticketId}`, data),

  previewEntry: (ticketId: string, data: {
    minutes: number;
    description: string;
    work_date: string;
    is_billable?: boolean;
    outside_service_hours?: boolean;
    activity_kind?:string;contract_id?:number;component_key?:string;work_started_at?:string;work_ended_at?:string;cost_approval_id?:number;timer_result_id?:number;visit_key?:string;travel_leg?:string;
  }) => apiClient.post(`/time-tracking/ticket/${ticketId}/preview`, data),
  
  updateEntry: (id: string, data: any) => 
    apiClient.put(`/time-tracking/entry/${id}`, data),
  
  deleteEntry: (id: string) => 
    apiClient.delete(`/time-tracking/entry/${id}`),
  
  getSummary: (params: {
    from_date?: string;
    to_date?: string;
    group_by: 'agent' | 'ticket' | 'customer';
  }) => 
    apiClient.get('/time-tracking/summary', { params }),

  exportCSV: (params?: {
    customer_id?: string;
    from_date?: string;
    to_date?: string;
    billable_only?: string;
  }) =>
    apiClient.get('/time-tracking/export/csv', { params, responseType: 'blob' }),
  
  getAgentRates: (agentId: string) => 
    apiClient.get(`/time-tracking/rates/${agentId}`),
  
  setAgentRate: (agentId: string, data: { rate: number; valid_from: string }) => 
    apiClient.post(`/time-tracking/rates/${agentId}`, data),

  // Timer persistence
  startTimer: (ticketId: string) =>
    apiClient.post('/time-tracking/timer/start', { ticket_id: ticketId }),
  stopTimer: (timerId?: number, ticket?: string) =>
    apiClient.post('/time-tracking/timer/stop', { timer_id: timerId }, ticketContextConfig(ticket)),
  getCurrentTimer: (ticket?: string) =>
    apiClient.get('/time-tracking/timer/current', ticketContextConfig(ticket)),

  // Quota status
  getTicketQuotaStatus: (ticketId: string) =>
    apiClient.get(`/time-tracking/ticket/${ticketId}/quota`),
};

/** Lexoffice / Lexware Public API (Entwurfsrechnungen, org-weit ein API-Key) */
export const lexofficeApi = {
  getSettings: () => apiClient.get('/integrations/lexoffice/settings'),
  updateSettings: (data: Record<string, unknown>) => apiClient.put('/integrations/lexoffice/settings', data),
  test: () => apiClient.post('/integrations/lexoffice/test'),
  createDraftInvoice: (data: {
    customer_id: number | string;
    from_date: string;
    to_date: string;
    contract_id?: number | string | null;
    hourly_net_rate?: number;
  }) => apiClient.post('/integrations/lexoffice/draft-invoice', data),
  getExports: (params?: { customer_id?: string }) =>
    apiClient.get('/integrations/lexoffice/exports', { params }),
};

/** MLOffice-Anbindung (global, eine Instanz je Installation) */
export const mlofficeApi = {
  getSettings: () => apiClient.get('/admin/mloffice/settings'),
  updateSettings: (data: Record<string, unknown>) => apiClient.put('/admin/mloffice/settings', data),
  test: () => apiClient.post('/admin/mloffice/test'),
  createLinkToken: (validityDays: number | null) => apiClient.post('/admin/mloffice/link-token', { validity_days: validityDays }),
  revokeLinkToken: () => apiClient.delete('/admin/mloffice/link-token'),
  // Kundenuebernahme
  getPartners: (refresh?: boolean) => apiClient.get('/admin/mloffice/partners', { params: refresh ? { refresh: '1' } : {} }),
  updateSelection: (data: { select?: string[]; deselect?: string[] }) => apiClient.put('/admin/mloffice/partners/selection', data),
  resolveConflict: (partnerId: string, data: { link?: number; create?: boolean; email?: string }) =>
    apiClient.post(`/admin/mloffice/partners/${encodeURIComponent(partnerId)}/resolve-conflict`, data),
  startSyncRun: () => apiClient.post('/admin/mloffice/sync-run'),
  getSyncStatus: () => apiClient.get('/admin/mloffice/sync-status'),
  // Zeiten-Uebertragung + Zuordnungen
  getProjects: (partnerId?: string) => apiClient.get('/admin/mloffice/projects', { params: partnerId ? { partnerId } : {} }),
  getUsers: () => apiClient.get('/admin/mloffice/users'),
  getUserMap: () => apiClient.get('/admin/mloffice/user-map'),
  updateUserMap: (map: Array<{ asd_user_id: number; mloffice_user_id: string | null }>) =>
    apiClient.put('/admin/mloffice/user-map', { map }),
  getProjectMap: (customerId: number) => apiClient.get('/admin/mloffice/project-map', { params: { customer_id: customerId } }),
  updateProjectMap: (data: { customer_id: number; contract_id: number; mloffice_project_id: string | null }) =>
    apiClient.put('/admin/mloffice/project-map', data),
  previewTransfer: (data: { customer_id: number; from: string; to: string }) =>
    apiClient.post('/admin/mloffice/transfer-runs/preview', data),
  createTransfer: (data: { customer_id: number; mloffice_project_id: string; contract_id?: number | null; from: string; to: string }) =>
    apiClient.post('/admin/mloffice/transfer-runs', data),
  listTransferRuns: (customerId?: number) =>
    apiClient.get('/admin/mloffice/transfer-runs', { params: customerId ? { customer_id: customerId } : {} }),
  getTransferRun: (id: number) => apiClient.get(`/admin/mloffice/transfer-runs/${id}`),
  retryOutbox: (id: number) => apiClient.post(`/admin/mloffice/outbox/${id}/retry`),
  skipOutbox: (id: number) => apiClient.post(`/admin/mloffice/outbox/${id}/skip`),
  // Abrechnung
  getBilling: (customerId: number) => apiClient.get(`/admin/mloffice/customers/${customerId}/billing`),
  refreshOpenItems: () => apiClient.post('/admin/mloffice/openitems/refresh'),
  getReconciliation: (customerId: number) => apiClient.get('/admin/mloffice/reconciliation', { params: { customer_id: customerId } }),
  createDraftInvoice: (customerId: number, data: { from: string; to: string; hourly_net_rate: number; tax_rate?: number }) =>
    apiClient.post(`/admin/mloffice/customers/${customerId}/draft-invoice`, data),
};

// Workflow API
export const workflowApi = {
  getAll: () => 
    apiClient.get('/workflows'),
  
  getById: (id: string) => 
    apiClient.get(`/workflows/${id}`),
  
  create: (data: {
    name: string;
    description?: string;
    trigger_type: string;
    trigger_config?: any;
    priority?: number;
    conditions?: Array<{
      condition_type: string;
      operator?: string;
      value?: string;
    }>;
    actions?: Array<{
      action_type: string;
      action_config?: any;
    }>;
  }) => 
    apiClient.post('/workflows', data),
  
  update: (id: string, data: any) => 
    apiClient.put(`/workflows/${id}`, data),
  
  delete: (id: string) => 
    apiClient.delete(`/workflows/${id}`),
  
  toggle: (id: string, is_active: boolean) => 
    apiClient.put(`/workflows/${id}/toggle`, { is_active }),
  
  getLogs: (workflowId: string, params?: { limit?: number; offset?: number }) => 
    apiClient.get(`/workflows/${workflowId}/logs`, { params }),
};

// Customer Portal API
export const customerApi = {
  getSession: () => customerApiClient.get('/customer-portal/session'),
  // Auth
  login: (credentials: { email: string; password: string; captchaToken?: string }) =>
    customerApiClient.post('/customer-portal/login', credentials),

  logout: () => customerApiClient.post('/customer-portal/logout'),

  checkSsoDomain: (email: string) =>
    publicApiClient.post('/auth/check-sso-domain', { email }),

  ssoCallback: (data: { idToken: string; email: string }) =>
    customerApiClient.post('/auth/sso/callback', data),

  requestPasswordReset: (email: string, captchaToken?: string) =>
    customerApiClient.post('/customer-portal/password-reset', { email, captchaToken }),

  confirmPasswordReset: (data: { token: string; newPassword: string; mfaCode?: string; captchaToken?: string }) =>
    customerApiClient.post('/customer-portal/password-reset/confirm', data),

  inspectInvitation: (token: string) =>
    customerApiClient.post('/customer-portal/invitation/inspect', { token }),

  acceptInvitation: (data: { token: string; newPassword: string }) =>
    customerApiClient.post('/customer-portal/invitation/accept', data),

  verifyMfa: (data: { code: string; mfaToken: string }) =>
    customerApiClient.post('/customer-portal/mfa/verify', data),

  setupEnforcedMfa: (mfaToken: string) =>
    customerApiClient.post('/customer-portal/mfa/setup-enforced', { mfaToken }),

  enableEnforcedMfa: (data: { mfaToken: string; code: string; secret: string }) =>
    customerApiClient.post('/customer-portal/mfa/enable-enforced', data),

  getMfaStatus: () => customerApiClient.get('/customer-portal/mfa/status'),

  setupMfa: () => customerApiClient.post('/customer-portal/mfa/setup'),

  enableMfa: (data: { code: string; secret: string }) =>
    customerApiClient.post('/customer-portal/mfa/enable', data),

  disableMfa: (data: { code: string; password: string }) =>
    customerApiClient.post('/customer-portal/mfa/disable', data),

  // Profile
  getProfile: () =>
    customerApiClient.get('/customer-portal/profile'),

  changePassword: (data: { currentPassword: string; newPassword: string }) =>
    customerApiClient.post('/customer-portal/change-password', data),

  // Tickets
  getMyTickets: (params?: { status?: string; search?: string; page?: number; limit?: number; sort?: string; cursor?: string; scope?: 'personal' | 'company' }, signal?: AbortSignal) =>
    customerApiClient.get('/customer-portal/tickets', { params, signal }),

  getTicketDetail: (id: string, params?: { replyPage?: number; replyLimit?: number; replyCursor?: string; attachmentPage?: number; attachmentLimit?: number }) =>
    customerApiClient.get(`/customer-portal/tickets/${id}`, { params }),

  createTicket: (
    data: {
      subject?: string;
      description?: string;
      priority?: string;
      category_id?: number | null;
      ticket_kind?: 'incident' | 'request';
      request_slug?: string;
      request_type_id?: number;
      affected_employee_id?: number;
      custom_fields?: Record<string, unknown>;
    },
    files?: File[],
    idempotencyKey: string = createIdempotencyKey(),
    captchaToken?: string,
  ) => {
    const formData = new FormData();
    formData.append('ticket_kind', data.ticket_kind || 'incident');
    if (data.subject != null) formData.append('subject', data.subject);
    if (data.description != null && String(data.description).trim() !== '') {
      formData.append('description', String(data.description));
    }
    if (data.priority) formData.append('priority', data.priority);
    if (data.category_id != null) {
      formData.append('category_id', String(data.category_id));
    }
    if (data.request_slug) formData.append('request_slug', data.request_slug);
    if (data.request_type_id != null) formData.append('request_type_id', String(data.request_type_id));
    if (data.affected_employee_id != null) formData.append('affected_employee_id', String(data.affected_employee_id));
    if (captchaToken) formData.append('captchaToken', captchaToken);
    if (data.custom_fields && Object.keys(data.custom_fields).length > 0) {
      formData.append('custom_fields', JSON.stringify(data.custom_fields));
    }
    if (files) {
      files.forEach((file) => formData.append('attachments', file));
    }
    return customerApiClient.post('/customer-portal/tickets', formData, {
      headers: { 'Content-Type': 'multipart/form-data', 'Idempotency-Key': idempotencyKey },
      timeout: 120000,
    });
  },

  getCategories: () =>
    customerApiClient.get('/customer-portal/categories'),

  replyToTicket: (id: string, message: string, idempotencyKey: string = createIdempotencyKey(), files: File[] = []) => {
    if (!files.length) return customerApiClient.post(`/customer-portal/tickets/${id}/reply`, { message }, { headers: { 'Idempotency-Key': idempotencyKey } });
    const form = new FormData(); form.append('message', message);
    files.forEach(file => form.append('attachments', file));
    return customerApiClient.post(`/customer-portal/tickets/${id}/reply`, form, { headers: { 'Idempotency-Key': idempotencyKey, 'Content-Type': 'multipart/form-data' } });
  },

  closeTicket: (id: string, idempotencyKey: string = createIdempotencyKey()) =>
    customerApiClient.post(`/customer-portal/tickets/${id}/close`, undefined, { headers: { 'Idempotency-Key': idempotencyKey } }),

  reopenTicket: (id: string, idempotencyKey: string = createIdempotencyKey()) =>
    customerApiClient.post(`/customer-portal/tickets/${id}/reopen`, undefined, { headers: { 'Idempotency-Key': idempotencyKey } }),

  getAttachmentDownloadUrl: (ticketId: string, attachmentId: number) =>
    `/api/customer-portal/tickets/${ticketId}/attachments/${attachmentId}/download`,

  // Profile management
  updateProfile: (data: {
    firstName?: string;
    pcName?: string;
    preferences?: {language?:string;defaultPriority?:string;reducedMotion?:boolean};
    lastName?: string;
    phone?: string;
    mobile?: string;
    department?: string;
    position?: string;
    location?: string;
  }) =>
    customerApiClient.put('/customer-portal/profile', data),

  uploadProfilePhoto: (file: File) => {
    const formData = new FormData();
    formData.append('photo', file);
    return customerApiClient.post('/customer-portal/profile/photo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  // Mini-Reporting
  getMyTicketStats: (scope: 'personal' | 'company' = 'personal') =>
    customerApiClient.get('/customer-portal/my-stats', { params: { scope } }),
  getCompanyOverview: (period: CompanyOverviewPeriod = '90d', signal?: AbortSignal) =>
    customerApiClient.get<{ success: boolean; overview: CompanyOverview }>('/customer-portal/company-overview', { params: { period }, signal }),

  // Intelligent Ticket Creation
  getTicketSuggestions: (q: string) =>
    customerApiClient.get('/customer-portal/ticket-suggestions', { params: { q } }),

  getSlaInfo: () =>
    customerApiClient.get('/customer-portal/sla-info'),

  aiImproveText: (text: string) =>
    customerApiClient.post('/customer-portal/ai-improve-text', { text }),

  // Contracts
  getMyContracts: () =>
    customerApiClient.get('/customer-portal/contracts'),
  getContractDetail: (id: number) =>
    customerApiClient.get(`/customer-portal/contracts/${id}`),
  getCostApprovals: (page = 1, signal?: AbortSignal) =>
    customerApiClient.get('/customer-portal/cost-approvals', { params: { page }, signal }),
  getCostApproval: (id: number, signal?: AbortSignal) =>
    customerApiClient.get(`/customer-portal/cost-approvals/${id}`, { signal }),
  decideCostApproval: (contractId: number, approvalId: number, decision: { kind: 'portal_confirmation' | 'rejected'; reason: string }) =>
    customerApiClient.post(`/customer-portal/contracts/${contractId}/cost-approvals/${approvalId}/decision`, decision),

  // Documents (read-only — managed by agents)
  getMyDocuments: (category?: string, search?: string, page = 1) =>
    customerApiClient.get('/customer-portal/documents', { params: { category, search, page, limit: 25 } }),
  getDocumentDownloadUrl: (id: number) =>
    `/api/customer-portal/documents/${id}/download`,
  getDocumentPreviewUrl: (id: number) =>
    `/api/customer-portal/documents/${id}/preview`,
};

// Notifications API
export const notificationsApi = {
  getAll: (params?: { limit?: number; unread_only?: boolean; status?: 'open' | 'acknowledged' | 'resolved'; source?: string; search?: string }) =>
    apiClient.get('/notifications', { params }),

  markAsRead: (id: string | number) =>
    apiClient.post(`/notifications/${id}/read`),

  markAllAsRead: () =>
    apiClient.post('/notifications/read-all'),

  updateIncidentStatus: (id: string | number, status: 'open' | 'acknowledged' | 'resolved') =>
    apiClient.patch(`/notifications/${id}/incident`, { status }),

  // Notification Settings
  getSettings: () =>
    apiClient.get('/notifications/settings'),

  updateSettings: (settings: Record<string, boolean>) =>
    apiClient.put('/notifications/settings', settings),
};

// Email Settings API (Admin)
export const emailSettingsApi = {
  // Admin settings
  getSettings: () =>
    apiClient.get('/email-settings/admin/email-settings'),

  updateSetting: (eventType: string, data: { is_enabled?: boolean; recipient_type?: string; template_id?: number; delay_minutes?: number }) =>
    apiClient.put(`/email-settings/admin/email-settings/${eventType}`, data),

  // Templates
  getTemplates: () =>
    apiClient.get('/email-settings/admin/email-templates'),

  getTemplate: (id: string) =>
    apiClient.get(`/email-settings/admin/email-templates/${id}`),

  createTemplate: (data: { event_type: string; name: string; subject: string; html_body: string; text_body?: string }) =>
    apiClient.post('/email-settings/admin/email-templates', data),

  updateTemplate: (id: string, data: { name?: string; subject?: string; html_body?: string; text_body?: string; is_active?: boolean }) =>
    apiClient.put(`/email-settings/admin/email-templates/${id}`, data),

  previewTemplate: (id: string, sampleData?: any) =>
    apiClient.post(`/email-settings/admin/email-templates/${id}/preview`, { sampleData }),

  testSendTemplate: (id: string, to?: string) =>
    apiClient.post(`/email-settings/admin/email-templates/${id}/test-send`, to ? { to } : {}),

  previewSystemEvent: (eventType: string) =>
    apiClient.post(`/email-settings/admin/system-email-events/${encodeURIComponent(eventType)}/preview`, {}),

  getSystemCustomization: (eventType: string) => apiClient.get(`/email-settings/admin/system-email-events/${encodeURIComponent(eventType)}/customization`),
  saveSystemCustomization: (eventType: string, data: { subject_prefix: string; introduction: string; closing: string }) => apiClient.put(`/email-settings/admin/system-email-events/${encodeURIComponent(eventType)}/customization`, data),

  testSendSystemEvent: (eventType: string, to?: string) =>
    apiClient.post(`/email-settings/admin/system-email-events/${encodeURIComponent(eventType)}/test-send`, to ? { to } : {}),
};

// Agent Email Settings API
export const agentEmailSettingsApi = {
  getMySettings: () =>
    apiClient.get('/email-settings/email-settings/my'),

  updateMySetting: (data: { event_type: string; is_enabled?: boolean; use_global?: boolean }) =>
    apiClient.put('/email-settings/email-settings/my', data),
};

// Dashboard / Reporting API
export const dashboardApi = {
  // `customerId` grenzt die Kennzahlen auf einen Kunden ein ('' = alle). Der Server
  // prueft die Zulaessigkeit noch einmal selbst (reportScopeFilter) — die Auswahl in
  // der Oberflaeche ist Bequemlichkeit, keine Rechtegrenze.
  getAgentMetrics: (range: '7d' | '30d' = '7d', customerId?: string) =>
    apiClient.get('/dashboard/agent/metrics', {
      params: customerId ? { range, customer_id: customerId } : { range },
    }),
  getAgentOverview: (range: '7d' | '30d' = '7d', customerId?: string) =>
    apiClient.get('/dashboard/agent/overview', {
      params: { range, ...(customerId ? { customer_id: customerId } : {}) },
    }),
  trackAction: (action: string, range: '7d' | '30d') =>
    apiClient.post('/product-analytics/events', {
      event: 'dashboard_action', metadata: { action, range },
    }),

  getAdminMetrics: (range: '7d' | '30d' = '7d') =>
    apiClient.get('/dashboard/admin/metrics', { params: { range } }),
};

// ITIL Reports API
export const reportsApi = {
  getOverview: (range: string, customerId?: number) =>
    apiClient.get('/dashboard/reports/overview', { params: { range, customer_id: customerId } }),
  getSla: (range: string, customerId?: number) =>
    apiClient.get('/dashboard/reports/sla', { params: { range, customer_id: customerId } }),
  getIncidents: (range: string, customerId?: number) =>
    apiClient.get('/dashboard/reports/incidents', { params: { range, customer_id: customerId } }),
  getAgents: (range: string) =>
    apiClient.get('/dashboard/reports/agents', { params: { range } }),
  getCustomers: (range: string, customerId?: number) =>
    apiClient.get('/dashboard/reports/customers', { params: { range, customer_id: customerId } }),
  getTime: (range: string, customerId?: number) =>
    apiClient.get('/dashboard/reports/time', { params: { range, customer_id: customerId } }),
  getTrends: (months: number, customerId?: number) =>
    apiClient.get('/dashboard/reports/trends', { params: { months, customer_id: customerId } }),
  getCsat: (range: string, customerId?: number) =>
    apiClient.get('/dashboard/reports/csat', { params: { range, customer_id: customerId } }),
  getSentiment: (range: string, customerId?: number) =>
    apiClient.get('/dashboard/reports/sentiment', { params: { range, customer_id: customerId } }),
  getAssets: (customerId?: number) =>
    apiClient.get('/dashboard/reports/assets', { params: { customer_id: customerId } }),
  getLicenses: (customerId?: number) =>
    apiClient.get('/dashboard/reports/licenses', { params: { customer_id: customerId } }),
  aiInterpret: (reportType: string, data: any) =>
    apiClient.post('/dashboard/reports/ai-interpret', { report_type: reportType, data }),
  exportReport: (reportType: string, format: string, data: any) =>
    apiClient.post('/dashboard/reports/export', { report_type: reportType, format, data }, { responseType: 'blob' }),
  getCustomReport: (payload: any) =>
    apiClient.post('/dashboard/reports/custom', payload),
};

// App Settings API
export const appSettingsApi = {
  getPublic: () =>
    publicApiClient.get('/app-settings/public'),

  getAll: () =>
    apiClient.get('/app-settings'),

  update: (settings: Record<string, any>) =>
    apiClient.put('/app-settings', settings),
};

// Attachment API
export const attachmentApi = {
  upload: (ticketId: string, file: File, progress?: (percent: number) => void, privateDraft = false) => {
    const formData = new FormData();
    formData.append('file', file);
    if (privateDraft) formData.append('reply_draft', '1');
    return apiClient.post(`/tickets/${ticketId}/attachments`, formData, {
      onUploadProgress: event => { if (event.total) progress?.(Math.round(event.loaded / event.total * 100)); },
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  uploadReply: (ticketId: string, replyId: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post(`/tickets/${ticketId}/replies/${replyId}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  /** Bereits hochgeladene Ticket-Anhänge einer Antwort zuordnen (nach addReply). */
  linkAttachmentsToReply: (ticketId: string, replyId: number, attachmentIds: number[]) =>
    apiClient.post(`/tickets/${ticketId}/replies/${replyId}/link-attachments`, {
      attachment_ids: attachmentIds,
    }),

  getTicketAttachments: (ticketId: string, includeReplyAttachments?: boolean) =>
    apiClient.get<{
      success: boolean;
      attachments: any[];
      reply_attachments?: { id: number; filename: string; mime_type: string; size: number; reply_id: number; cloud_deliveries?: any[] }[];
    }>(`/tickets/${ticketId}/attachments`, {
      params: includeReplyAttachments ? { include_reply_attachments: '1' } : undefined,
    }),

  download: (attachmentId: string) =>
    apiClient.get(`/attachments/${attachmentId}/download`, { responseType: 'blob' }),

  previewUrl: (attachmentId: string) => {
    const ref = ticketReference(attachmentId);
    return ref ? `/api/attachments/${ref.ticketId}/preview?customer_id=${ref.customerId}` : `/api/attachments/${attachmentId}/preview`;
  },

  getPreviewToken: (attachmentId: string) =>
    apiClient.get<{ success: boolean; token: string }>(`/attachments/${attachmentId}/token`),

  delete: (attachmentId: string) =>
    apiClient.delete(`/attachments/${attachmentId}`),

  importCloud: (ticketId: string, token: string, customerId?: string | number) =>
    apiClient.post(`/admin/tickets/${ticketId}/attachments/cloud-import`, {
      token,
      ...(customerId ? { customer_id: customerId } : {}),
    }),

  retryCloudDelivery: (attachmentId: string | number, deliveryId: string | number, customerId?: string | number) =>
    apiClient.post(`/attachments/${attachmentId}/cloud-deliveries/${deliveryId}/retry`, {
      ...(customerId ? { customer_id: customerId } : {}),
    }),
};

export interface StagedCloudFileResponse {
  success: boolean;
  status: 'completed';
  token: string;
  name: string;
  mime_type: string;
  size: number;
  provider: 'nextcloud' | 'sharepoint';
  expires_at: string;
}

export const cloudFilesApi = {
  stage: (file: { source: string; url: string; name: string; mimeType?: string }, customerId?: string | number) =>
    apiClient.post<StagedCloudFileResponse>('/admin/cloud-files/stage', {
      provider: file.source,
      remote_path: file.url,
      name: file.name,
      mime_type: file.mimeType,
      ...(customerId ? { customer_id: customerId } : {}),
    }),
};

// Knowledge Base API
export const kbApi = {
  // Categories (admin + agent)
  getCategories: () => apiClient.get('/kb/categories'),
  importCategories: (document: unknown) => apiClient.post('/kb/categories/import', document),
  createCategory: (data: any) => apiClient.post('/kb/categories', data),
  updateCategory: (id: number, data: any) => apiClient.put(`/kb/categories/${id}`, data),
  /** `zielKategorieId` ist nötig, sobald noch Artikel an der Kategorie hängen (409). */
  deleteCategory: (id: number, zielKategorieId?: number) =>
    apiClient.delete(`/kb/categories/${id}`, {
      data: zielKategorieId ? { ziel_kategorie_id: zielKategorieId } : undefined,
    }),
  getCategoryCustomers: (id: number) => apiClient.get(`/kb/categories/${id}/customers`),
  assignCategoryCustomers: (id: number, customer_ids: number[]) => apiClient.put(`/kb/categories/${id}/customers`, { customer_ids }),

  // Articles (agent)
  getArticles: (params?: { category_id?: number; status?: string; search?: string; page?: number; limit?: number }) =>
    apiClient.get('/kb/articles', { params }),
  getOverview: () => apiClient.get('/kb/overview'),
  getArticle: (id: number) => apiClient.get(`/kb/articles/${id}`),
  createArticle: (data: any) => apiClient.post('/kb/articles', data),
  /**
   * `data.basiert_auf` ist die Fassungsnummer, die beim Laden galt. Weicht sie ab, hat
   * jemand anders zwischenzeitlich gespeichert und der Server antwortet mit 409 statt zu
   * überschreiben. `data.grund` unterscheidet manuelles von automatischem Speichern.
   */
  updateArticle: (id: number, data: any) => apiClient.put(`/kb/articles/${id}`, data),
  deleteArticle: (id: number) => apiClient.delete(`/kb/articles/${id}`),
  updateLifecycle: (id: number, data: { owner_id?: number | null; reviewer_id?: number | null; review_due_at?: string | null; valid_until?: string | null; scheduled_publish_at?: string | null; locale?: string }) =>
    apiClient.patch(`/kb/articles/${id}/lifecycle`, data),
  reviewAction: (id: number, action: string, data?: Record<string, unknown>) =>
    apiClient.post(`/kb/articles/${id}/review-action`, { action, ...data }),
  addCandidateFromTicket: (ticketId: number, reason?: string) =>
    apiClient.post(`/kb/candidates/from-ticket/${ticketId}`, { reason }),
  updateCandidate: (id: number, data: { status: 'open' | 'accepted' | 'dismissed' | 'converted'; assigned_to?: number | null; article_id?: number | null }) =>
    apiClient.patch(`/kb/candidates/${id}`, data),

  /** Bild aus dem Editor hochladen. Antwortet mit einer Adresse, die Anmeldung verlangt. */
  ladeBildHoch: (formular: FormData) =>
    apiClient.post('/kb/bilder', formular, { headers: { 'Content-Type': 'multipart/form-data' } }),

  /** Artikel als PDF. Wird serverseitig erzeugt und bereinigt. */
  getPdf: (id: number) => apiClient.get(`/kb/articles/${id}/pdf`, { responseType: 'blob' }),

  // Fassungen
  getVersions: (id: number) => apiClient.get(`/kb/articles/${id}/versions`),
  getVersion: (id: number, versionId: number) => apiClient.get(`/kb/articles/${id}/versions/${versionId}`),
  restoreVersion: (id: number, versionId: number) =>
    apiClient.post(`/kb/articles/${id}/versions/${versionId}/restore`),

  // AI-powered KB
  getAiStatus: () => apiClient.get('/kb/ai/status'),
  aiGenerate: (data: { topic: string; keywords?: string; category_id?: number; language?: string; length?: 'short' | 'medium' | 'long'; style?: 'technical' | 'simple' | 'end_user' | 'formal' | 'balanced' }) =>
    apiClient.post('/kb/ai/generate', data),
  aiImprove: (data: { content: string; instruction?: string }) =>
    apiClient.post('/kb/ai/improve', data),
  aiSummarize: (data: { content: string }) =>
    apiClient.post('/kb/ai/summarize', data),
  aiFromTicket: (data: { ticket_id: number }) =>
    apiClient.post('/kb/ai/from-ticket', data),
};

// Customer Portal KB API
export const customerKbApi = {
  getCategories: () => customerApiClient.get('/customer-portal/kb/categories'),
  getArticles: (params?: { category_id?: number; category_ids?: string; search?: string; page?: number; limit?: number }) =>
    customerApiClient.get('/customer-portal/kb/articles', { params }),
  getArticle: (idOrSlug: number | string) => customerApiClient.get(`/customer-portal/kb/articles/${encodeURIComponent(idOrSlug)}`),
  getSubscriptionStatus: (id: number) => customerApiClient.get(`/customer-portal/kb/articles/${id}/subscription-status`),
  subscribe: (id: number) => customerApiClient.post(`/customer-portal/kb/articles/${id}/subscribe`),
  unsubscribe: (id: number) => customerApiClient.post(`/customer-portal/kb/articles/${id}/unsubscribe`),
  getFeedback: (id: number) => customerApiClient.get(`/customer-portal/kb/articles/${id}/feedback`),
  submitFeedback: (id: number, data: { rating: 'yes' | 'partial' | 'no'; reason?: string; comment?: string; search_query?: string }) =>
    customerApiClient.post(`/customer-portal/kb/articles/${id}/feedback`, data),
  recordSearch: (data: { query: string; result_count: number; opened_article_id?: number; outcome?: string }) =>
    customerApiClient.post('/customer-portal/kb/search-events', data),
};

// Contract Management API
export const contractApi = {
  getSlaDefault: (customerId: string | number) => apiClient.get(`/admin/customers/${customerId}/sla-default-contract`),
  setSlaDefault: (customerId: string | number, contract: { id: number; schema: string } | null) => apiClient.put(`/admin/customers/${customerId}/sla-default-contract`, { contract }),
  getForCustomer: (customerId: number, includeInactive = false) =>
    apiClient.get(`/admin/customers/${customerId}/contracts`, { params: { includeInactive } }),
  getById: (id: number) => apiClient.get(`/admin/contracts/${id}`),
  create: (data: any) => apiClient.post('/admin/contracts', data),
  update: (id: number, data: any) => apiClient.put(`/admin/contracts/${id}`, data),
  getStats: () => apiClient.get('/admin/contracts/stats'),
  getExpiring: () => apiClient.get('/admin/contracts/expiring'),
};

// Nextcloud Talk API
export const talkApi = {
  createRoom: (name: string) => apiClient.post('/tickets/talk/create-room', { name }),
  createTicketMeeting: (ticketId: string) => apiClient.post(`/tickets/${ticketId}/talk-meeting`),
};

// Calendar API
export const calendarApi = {
  getEvents: (from: string, to: string, calendar?: string) =>
    apiClient.get('/calendar/events', { params: { from, to, calendar } }),
  getCalendars: () => apiClient.get('/calendar/calendars'),
  createEvent: (data: { title: string; start: string; end: string; description?: string; location?: string; calendar?: string }) =>
    apiClient.post('/calendar/events', data),
  createMeeting: (data: {
    title: string;
    start: string;
    duration: number;
    provider: 'talk' | 'teams';
    attendees?: string[];
    talkParticipantIds?: string[];
    calendar?: string;
    ticketId?: string;
    ticketNumber?: string;
  }) => apiClient.post('/calendar/meeting', data),
};

// Import API (Admin)
export const importApi = {
  upload: (file: File, parser: string, entityType?: string) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('parser', parser);
    if (entityType) {
      formData.append('entityType', entityType);
    }
    return apiClient.post('/admin/import/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  catalog: () => apiClient.get('/admin/import/catalog'),
  preview: (uploadId: string) => apiClient.get(`/admin/import/preview?uploadId=${encodeURIComponent(uploadId)}`),
  /** Startet die Übernahme. Antwortet sofort mit `jobId` — der Lauf selbst dauert. */
  execute: (uploadId: string, mapping: Record<string, string>, targetCustomerId?: number | null) =>
    apiClient.post('/admin/import/execute', { uploadId, mapping, targetCustomerId: targetCustomerId ?? null }),
  /** Ohne jobId der zuletzt gestartete Lauf — so findet ein neu geladenes Fenster zurück. */
  status: (jobId?: number) =>
    apiClient.get('/admin/import/status', { params: jobId ? { jobId } : {} }),
  downloadTemplate: (entityType: string) =>
    apiClient.get(`/admin/import/templates/${entityType}`, { responseType: 'blob' }),
  exportData: (entityType: string, includeEmployees?: boolean) =>
    apiClient.get(`/admin/export/${entityType}?includeEmployees=${!!includeEmployees}`, { responseType: 'blob' }),
};

// Enrichment API (Agent + Admin über /customers; Admin-Dashboard kann gleiche Endpunkte nutzen)
export const enrichmentApi = {
  enrich: (customerId: number) => apiClient.post(`/customers/${customerId}/enrich`),
  apply: (customerId: number, fields: string[]) =>
    apiClient.post(`/customers/${customerId}/enrichment/apply`, { fields }),
};

// Document Management API (Agent + Admin über /api/customers)
export const documentApi = {
  getForCustomer: (customerId: number, contractId?: number) =>
    apiClient.get(`/customers/${customerId}/documents`, { params: { contract_id: contractId } }),
  upload: (customerId: number, file: File, data: { category?: string; description?: string; visible_to_company?: boolean; visible_to_employees?: string; contract_id?: number | null }) => {
    const formData = new FormData();
    formData.append('file', file);
    if (data.category) formData.append('category', data.category);
    if (data.description) formData.append('description', data.description);
    formData.append('visible_to_company', data.visible_to_company ? '1' : '0');
    if (data.visible_to_employees) formData.append('visible_to_employees', data.visible_to_employees);
    if (data.contract_id) formData.append('contract_id', String(data.contract_id));
    return apiClient.post(`/customers/${customerId}/documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  importCloud: (customerId: number, token: string, data: { category?: string; description?: string; visible_to_company?: boolean; visible_to_employees?: string; contract_id?: number | null }) =>
    apiClient.post(`/customers/${customerId}/documents/cloud-import`, { token, customer_id: customerId, ...data }),
  update: (id: number, data: any, customerId?: number) => apiClient.put(`/customers/documents/${id}`, data, { params: { customer_id: customerId } }),
  delete: (id: number, customerId?: number) => apiClient.delete(`/customers/documents/${id}`, { params: { customer_id: customerId } }),
  getDownloadUrl: (id: number, customerId?: number) => `/api/customers/documents/${id}/download${customerId ? `?customer_id=${customerId}` : ''}`,
  getPreviewUrl: (id: number, customerId?: number) => `/api/customers/documents/${id}/preview${customerId ? `?customer_id=${customerId}` : ''}`,
};

// Admin SSO Settings API
export const adminSsoApi = {
  get: () => apiClient.get('/admin/sso-settings'),
  update: (settings: any) => apiClient.put('/admin/sso-settings', settings),
  validate: () => apiClient.post('/admin/sso-settings/validate'),
  enable: (enable: boolean) => apiClient.post('/admin/sso-settings/enable', { enable }),
};

// Dashboard Config API
export const dashboardConfigApi = {
  get: () => apiClient.get('/dashboard/config'),
  update: (scope: string, config: any, scopeId?: string) =>
    apiClient.put('/dashboard/config', { scope, scope_id: scopeId, config }),
  listAll: () => apiClient.get('/dashboard/admin/configs'),
};

// AI Summary API
export const aiSummaryApi = {
  getSummary: (ticketId: string, regenerate = false) =>
    apiClient.get(`/tickets/${ticketId}/summary${regenerate ? '?regenerate=true' : ''}`),
  invalidate: (ticketId: string) =>
    apiClient.post(`/tickets/${ticketId}/summary/invalidate`),
};

// AI Settings API
export const aiSettingsApi = {
  get: () => apiClient.get('/admin/ai-settings'),
  update: (data: any) => apiClient.put('/admin/ai-settings', data),
  testConnection: (data?: any) => apiClient.post('/admin/ai-settings/test', data),
  /** Token accounting + cost estimate for the KI-Verbrauch / Consumption panel. */
  getConsumption: (days = 30) => apiClient.get('/admin/ai-settings/consumption', { params: { days } }),
};

// Integrations Framework API
export const integrationsApi = {
  list: () => apiClient.get('/admin/integrations'),
  get: (key: string) => apiClient.get(`/admin/integrations/${encodeURIComponent(key)}`),
  update: (key: string, data: { enabled?: boolean; config?: any }) =>
    apiClient.put(`/admin/integrations/${encodeURIComponent(key)}`, data),
  toggle: (key: string, enabled: boolean) =>
    apiClient.post(`/admin/integrations/${encodeURIComponent(key)}/toggle`, { enabled }),
  test: (key: string) =>
    apiClient.post(`/admin/integrations/${encodeURIComponent(key)}/test`),
  syncRun: (key: string, dryRun = false) =>
    apiClient.post(`/admin/integrations/${encodeURIComponent(key)}/sync-run`, { dryRun }),
  syncRuns: (key: string, limit = 20) =>
    apiClient.get(`/admin/integrations/${encodeURIComponent(key)}/sync-runs`, { params: { limit } }),
  health: () => apiClient.get('/admin/integrations/health'),
  getOpenRouterModels: (apiKey?: string) =>
    apiClient.get('/admin/integrations/ai.openrouter/models', { params: { api_key: apiKey } }),
  getProviderModels: (provider: string, apiKey?: string, endpointUrl?: string) =>
    apiClient.get(`/admin/integrations/ai-provider/${encodeURIComponent(provider)}/models`, {
      params: { api_key: apiKey, endpoint_url: endpointUrl },
    }),
  getAiPolicy: () => apiClient.get('/admin/integrations/ai-policy'),
  setAiPolicy: (preset: string) => apiClient.put('/admin/integrations/ai-policy', { preset }),
  getActiveViewers: () => apiClient.get<{ success: boolean; viewers: Record<string, boolean>; configs?: Record<string, { base_url?: string }> }>('/integrations/active-viewers'),
  webhookDeliveries: (limit?: number) => apiClient.get(`/admin/integrations/webhooks/deliveries${limit ? `?limit=${limit}` : ''}`),
  retryWebhookDelivery: (id: number) => apiClient.post(`/admin/integrations/webhooks/deliveries/${id}/retry`),
  retryAllDeadLetters: () => apiClient.post('/admin/integrations/webhooks/deliveries/retry-all'),
  integrationActions: (limit = 100) => apiClient.get('/admin/integrations/actions', { params: { limit } }),
  retryIntegrationAction: (id: number) => apiClient.post(`/admin/integrations/actions/${id}/retry`),
  /** monitoring.uptime — requires integration enabled + API key */
  getUptimeMonitors: () => apiClient.get('/admin/integrations/uptime/monitors'),
  getMailboxIngestionHealth: () => apiClient.get('/admin/integrations/mailbox-ingestion/health'),
  getEventReference: () => apiClient.get('/admin/integrations/event-reference'),
};

// Email Sources (Virtual Agents) API
export const emailSourcesApi = {
  list: () => apiClient.get('/admin/email-sources'),
  get: (id: number) => apiClient.get(`/admin/email-sources/${id}`),
  create: (data: any) => apiClient.post('/admin/email-sources', data),
  update: (id: number, data: any) => apiClient.put(`/admin/email-sources/${id}`, data),
  delete: (id: number) => apiClient.delete(`/admin/email-sources/${id}`),
  testParse: (id: number, sampleText: string) =>
    apiClient.post(`/admin/email-sources/${id}/test-parse`, { sample_text: sampleText }),
};

// Mail Log API (Admin)
export const mailLogApi = {
  getLogs: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return apiClient.get(`/admin/mail-log${qs}`);
  },
};

// System Readiness API (Admin)
export const systemReadinessApi = {
  getReport: () => apiClient.get('/admin/system-status/readiness'),
  downloadMarkdown: () => apiClient.get('/admin/system-status/readiness?format=markdown', { responseType: 'blob' }),
  verifyLedger: () => apiClient.get('/admin/system-status/verify-ledger'),
};

// AI Prompt Preconfigs API (Admin)
export const aiPromptsApi = {
  list: () => apiClient.get('/admin/ai-prompts'),
  get: (id: number) => apiClient.get(`/admin/ai-prompts/${id}`),
  create: (data: any) => apiClient.post('/admin/ai-prompts', data),
  update: (id: number, data: any) => apiClient.put(`/admin/ai-prompts/${id}`, data),
  delete: (id: number) => apiClient.delete(`/admin/ai-prompts/${id}`),
};

// Canned Responses API
export const cannedResponseApi = {
  getAll: () => apiClient.get('/admin/canned-responses'),
  /** `kb_article_id` hält fest, aus welchem Wissensartikel der Baustein geschnitten wurde. */
  create: (data: { title: string; content: string; shortcut?: string; category?: string; is_shared?: number; kb_article_id?: number }) =>
    apiClient.post('/admin/canned-responses', data),
  update: (id: number, data: { title: string; content: string; shortcut?: string; category?: string; is_shared?: number }) =>
    apiClient.put(`/admin/canned-responses/${id}`, data),
  delete: (id: number) => apiClient.delete(`/admin/canned-responses/${id}`),
};

// AI Solution Assistant API (Agent-facing)
export const aiAssistantApi = {
  getPreconfigs: (ticketId: string) => apiClient.get(`/tickets/${ticketId}/ai-assist/preconfigs`),
  requestSolution: (ticketId: string, preconfigId: number, customPrompt?: string) =>
    apiClient.post(`/tickets/${ticketId}/ai-assist`, { preconfigId, customPrompt }),
  getAdminSettings: () => apiClient.get('/admin/ai-assistant-settings'),
  updateAdminSettings: (data: any) => apiClient.put('/admin/ai-assistant-settings', data),
  getPublicSettings: () => apiClient.get('/tickets/ai-assistant/settings'),
  chat: (messages: { role: 'user' | 'assistant'; content: string }[], pageContext?: any) =>
    apiClient.post('/tickets/ai-assistant/chat', { messages, pageContext }),
};

// AI Feature Status (for all authenticated users)
export const aiFeaturesApi = {
  getStatus: () => apiClient.get('/tickets/ai-features/status'),
  categorize: (ticketId: string) => apiClient.post(`/tickets/${ticketId}/ai-categorize`),
  sentiment: (ticketId: string) => apiClient.post(`/tickets/${ticketId}/ai-sentiment`),
};

// Ticket Creation Context (agent-facing)
export const ticketCreationApi = {
  getContext: (params: { customer_id?: string; category_id?: string; priority?: string; q?: string }) =>
    apiClient.get('/tickets/creation-context', { params }),
  aiImproveText: (text: string) =>
    apiClient.post('/tickets/ai-improve-text', { text }),
};

// ─── AI Expansion APIs (Modul 1-7) ──────────────────────────────────

export const aiIntakeApi = {
  getMetadata: (ticketId: string) => apiClient.get(`/tickets/${ticketId}/ai-metadata`),
  retryInitial: (ticketId: string) => apiClient.post(`/tickets/${ticketId}/ai-intake/retry`),
};

export const aiComposeApi = {
  compose: (ticketId: string, options: { tone?: string; length?: string; includeKb?: boolean }) =>
    apiClient.post(`/tickets/${ticketId}/ai-compose`, options),
  suggestCanned: (ticketId: string) => apiClient.post(`/tickets/${ticketId}/ai-suggest-canned`),
  internalNote: (ticketId: string, noteType?: string) =>
    apiClient.post(`/tickets/${ticketId}/ai-internal-note`, { noteType: noteType || 'handover' }),
  adjustTone: (text: string, targetTone: string) =>
    apiClient.post('/tickets/ai-adjust-tone', { text, targetTone }),
};

export const aiPredictionsApi = {
  getTicketPredictions: (ticketId: string) => apiClient.get(`/tickets/${ticketId}/ai-predictions`),
  getHighRiskTickets: (threshold?: number) => apiClient.get('/tickets/ai-risk-tickets', { params: { threshold } }),
  acknowledge: (predictionId: number) => apiClient.post(`/tickets/ai-predictions/${predictionId}/acknowledge`),
  getCustomerRisk: (customerId: string) => apiClient.get(`/dashboard/ai-customer-risk/${customerId}`),
};

export const aiContextApi = {
  getContext: (ticketId: string, refresh = false) =>
    apiClient.get(`/tickets/${ticketId}/ai-context`, { params: refresh ? { refresh: true } : undefined }),
  getKbSuggestion: (ticketId: string) => apiClient.get(`/tickets/${ticketId}/ai-kb-suggestion`),
  gapAnalysis: () => apiClient.post('/kb/ai/gap-analysis'),
  semanticSearch: (q: string) => apiClient.post('/kb/ai/semantic-search', { query: q }),
};

export const aiCopilotApi = {
  getBriefing: () => apiClient.get('/tickets/ai-copilot/briefing'),
  getNextAction: () => apiClient.get('/tickets/ai-copilot/next-action'),
  getTrends: (days?: number) => apiClient.post('/dashboard/ai-copilot/trends', { days: days || 30 }),
};

export const customerAiApi = {
  getStatus: () => customerApiClient.get('/customer-portal/ai-status'),
  preAnswer: (subject: string, description: string) =>
    customerApiClient.post('/customer-portal/ai-pre-answer', { subject, description }),
  enrichForm: (subject: string, description: string) =>
    customerApiClient.post('/customer-portal/ai-enrich-form', { subject, description }),
};

// Operator Account API (admin only)
export const operatorAccountApi = {
  get: () => apiClient.get('/admin/account'),
  update: (data: Record<string, unknown>) => apiClient.put('/admin/account', data),
};

export const organizationApi = {
  departments: () => apiClient.get('/admin/org/departments'),
  createDepartment: (body: { name: string; code?: string; parent_id?: number | null; sort_order?: number }) =>
    apiClient.post('/admin/org/departments', body),
  updateDepartment: (id: number, body: { name?: string; code?: string; parent_id?: number | null; sort_order?: number }) =>
    apiClient.put(`/admin/org/departments/${id}`, body),
  deleteDepartment: (id: number) => apiClient.delete(`/admin/org/departments/${id}`),
  closures: () => apiClient.get('/admin/org/closures'),
  createClosure: (body: { closure_date: string; closure_type?: string; description?: string }) =>
    apiClient.post('/admin/org/closures', body),
  deleteClosure: (id: number) => apiClient.delete(`/admin/org/closures/${id}`),
  syncHolidays: (params?: { year?: number; region?: string }) =>
    apiClient.get('/admin/org/closures/sync-holidays', { params }),
  teams: () => apiClient.get('/admin/org/teams'),
  createTeam: (body: { name: string; description?: string }) => apiClient.post('/admin/org/teams', body),
  updateTeam: (id: number, body: { name?: string; description?: string }) => apiClient.put(`/admin/org/teams/${id}`, body),
  deleteTeam: (id: number) => apiClient.delete(`/admin/org/teams/${id}`),
  addTeamMember: (teamId: number, user_id: number) =>
    apiClient.post(`/admin/org/teams/${teamId}/members`, { user_id }),
  removeTeamMember: (teamId: number, userId: number) =>
    apiClient.delete(`/admin/org/teams/${teamId}/members/${userId}`),
  teamCustomers: (teamId: number) => apiClient.get(`/admin/org/teams/${teamId}/customers`),
  assignTeamCustomer: (teamId: number, customer_id: number) =>
    apiClient.post(`/admin/org/teams/${teamId}/customers`, { customer_id }),
  removeTeamCustomer: (teamId: number, customerId: number) =>
    apiClient.delete(`/admin/org/teams/${teamId}/customers/${customerId}`),
};

export const portalSettingsApi = {
  get: () => apiClient.get('/admin/portal-settings'),
  update: (body: Record<string, unknown>) => apiClient.put('/admin/portal-settings', body),
  getPreviewCustomers: () => apiClient.get('/admin/portal-settings/preview/customers'),
  getPreviewEmployees: (customerId: number) =>
    apiClient.get(`/admin/portal-settings/preview/customers/${customerId}/employees`),
  startPreview: (customerId: number, employeeId: number) =>
    apiClient.post('/admin/portal-settings/preview/session', {
      customer_id: customerId,
      employee_id: employeeId,
    }),
  uploadLogo: (file: File) => {
    const fd = new FormData();
    fd.append('logo', file);
    return apiClient.post('/admin/portal-settings/logo', fd, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  deleteLogo: () => apiClient.delete('/admin/portal-settings/logo'),
};

export const requestTypesApi = {
  list: () => apiClient.get('/admin/request-types'),
  get: (id: number) => apiClient.get(`/admin/request-types/${id}`),
  create: (body: Record<string, unknown>) => apiClient.post('/admin/request-types', body),
  update: (id: number, body: Record<string, unknown>) => apiClient.put(`/admin/request-types/${id}`, body),
  delete: (id: number) => apiClient.delete(`/admin/request-types/${id}`),
};

export const publicRequestTypesApi = {
  list: () => apiClient.get('/public/request-types'),
  getBySlug: (slug: string, signal?: AbortSignal) =>
    apiClient.get(`/public/request-types/${encodeURIComponent(slug)}`, { signal }),
};

/** Öffentliche Portal-Shell (ohne Auth) — nutzt dieselbe baseURL wie apiClient (VITE_API_BASE_URL oder `/api`). */
export type PublicPortalConfig = {
  portal_title?: string;
  portal_base_path?: string;
  customer_logo_url?: string | null;
  logo_href?: string | null;
  favicon_url?: string | null;
  default_language?: string;
  layout?: { widgets?: { id: string; type: string; column: number; order: number }[] };
  theme?: { primary?: string; preset?: string; mode?: 'light' | 'dark' | 'system' };
  branding_allowed?: boolean;
  footer_text?: string;
  error?: string;
};

export const publicPortalApi = {
  getConfig: () => apiClient.get<PublicPortalConfig>('/public/portal-config'),
};

export const channelSettingsApi = {
  list: () => apiClient.get('/admin/channels'),
  update: (key: string, body: { enabled?: boolean; config?: Record<string, unknown> }) =>
    apiClient.put(`/admin/channels/${key}`, body),
};

export const agentExtendedApi = {
  getProfile: (id: number) => apiClient.get(`/admin/agents/${id}/profile`),
  updateProfile: (id: number, body: Record<string, unknown>) => apiClient.put(`/admin/agents/${id}/profile`, body),
  metrics: (id: number, days?: number) => apiClient.get(`/admin/agents/${id}/metrics`, { params: { days } }),
};

// License API (admin only)
export const licenseApi = {
  getState: () => apiClient.get('/admin/license'),
  /**
   * Schlanker Lizenzstatus für die Oberflächen-Entscheidung „welche Module zeigen?".
   * Ohne `admin:system` erreichbar — `getState` liegt hinter dieser Berechtigung und
   * schlug für Agenten mit 403 fehl, wodurch SAM/Problems immer als nicht lizenziert
   * erschienen. Liefert nur Feature-Flags, Tarif und Status.
   */
  getFeatures: () => apiClient.get('/license/features'),
  setKey: (license_key: string) => apiClient.post('/admin/license/key', { license_key }),
  refresh: () => apiClient.post('/admin/license/refresh'),
  startTrial: () => apiClient.post('/admin/license/start-trial'),
  createCheckoutSession: (return_url?: string) => apiClient.post('/admin/license/checkout-session', { return_url }),
  checkoutReturn: (state: string) =>
    apiClient.get('/admin/license/checkout-return', { params: { state } }),
  getAvailablePlans: () => apiClient.get('/admin/license/plans'),
  createUpgradeCheckout: (plan: string, seats: number, return_url?: string, packages?: string[]) =>
    apiClient.post('/admin/license/upgrade-checkout', { plan, seats, return_url, packages }),
};
export const updateApi = {
  getStatus: () => apiClient.get('/admin/updates/status'),
  check: () => apiClient.post('/admin/updates/check'),
  apply: (version?: string) => apiClient.post('/admin/updates/apply', { version }),
  getChangelog: (from: string, to: string) => apiClient.get('/admin/updates/changelog', { params: { from, to } }),
  trigger: (version: string) => apiClient.post('/admin/updates/trigger', { version }),
  getQueueStatus: () => apiClient.get('/admin/updates/queue-status'),
  getHistory: () => apiClient.get('/admin/updates/history'),
};

// Notification Config API (admin only)
export const notificationConfigApi = {
  getConfig: () => apiClient.get('/admin/notification-config'),
  updateConfig: (configs: any[]) => apiClient.put('/admin/notification-config', { configs }),
};

// Active Integrations Status (all authenticated users)
export const activeIntegrationsApi = {
  getActive: () => apiClient.get('/tickets/integrations/active'),
};

// Maps / Geocoding (uses maps.openstreetmap integration)
export const mapsApi = {
  geocode: (q: string) => apiClient.get('/maps/geocode', { params: { q } }),
};

// Integration Features API (for agents, authenticated)
export const integrationFeaturesApi = {
  initiateCall: (phoneNumber: string, extension?: string) =>
    apiClient.post('/tickets/integrations/call', { phoneNumber, extension }),
  listStorageFiles: (provider: string, remotePath?: string) =>
    apiClient.get(`/tickets/integrations/storage/${provider}/files`, { params: { path: remotePath } }),
  lookupCredentials: (search: string, customerId: number | string, signal?: AbortSignal) =>
    apiClient.get('/tickets/integrations/credentials', { params: { search, customer_id: customerId }, signal }),
  revealCredential: (id: string, customerId: number | string, password: string, mfaCode?: string) =>
    apiClient.post(`/tickets/integrations/credentials/${encodeURIComponent(id)}/reveal`, { customer_id: customerId, password, mfaCode }),
};

// Report Export API (Admin)
export const reportExportApi = {
  getMonthlyReport: (customerId: number, month: string, format: 'html' | 'pdf' | 'json') =>
    apiClient.get('/admin/reports/monthly', {
      params: { customer_id: customerId, month, format },
      responseType: format === 'pdf' ? 'blob' : (format === 'html' ? 'text' : 'json'),
    }),
  getReportCustomers: () =>
    apiClient.get('/admin/reports/customers'),
};

// Contracts Admin API
export const contractsAdminApi = {
  getAll: (params?: { status?: string; customer_id?: string; contract_type?: string }) =>
    apiClient.get('/admin/contracts', { params }),
  getById: (id: number) => apiClient.get(`/admin/contracts/${id}`),
  getForCustomer: (customerId: number) => apiClient.get(`/admin/customers/${customerId}/contracts`),
  create: (data: any) => apiClient.post('/admin/contracts', data),
  update: (id: number, data: any) => apiClient.put(`/admin/contracts/${id}`, data),
  getStats: () => apiClient.get('/admin/contracts/stats'),
  getExpiring: (days?: number) => apiClient.get('/admin/contracts/expiring', { params: { days } }),
};

// GDPR / DSGVO Compliance API (admin only)
export const gdprApi = {
  getOverview: () => apiClient.get('/admin/gdpr/overview'),
  getRetention: () => apiClient.get('/admin/gdpr/retention'),
  updateRetention: (config: {
    enabled?: boolean;
    closed_ticket_days?: number;
    resolved_ticket_days?: number;
    anonymize_on_cleanup?: boolean;
    security_audit_log_retention_days?: number;
    api_token_access_log_retention_days?: number;
    email_log_retention_days?: number;
    system_log_retention_days?: number;
    ai_privacy_level?: 'normal' | 'strict';
  }) =>
    apiClient.put('/admin/gdpr/retention', config),
  runRetentionCleanup: () => apiClient.post('/admin/gdpr/retention/run'),
  anonymizeCustomer: (customerId: number, reason: string, confirmation: string) =>
    apiClient.post(`/admin/gdpr/anonymize/${customerId}`, { reason, confirmation }),
  exportCustomerData: (customerId: number) =>
    apiClient.get(`/admin/gdpr/export/${customerId}`, { responseType: 'blob' }),
  createCustomerExport: (customerId: number, key: string) =>
    apiClient.post(`/admin/gdpr/exports/${customerId}`, undefined, { headers: { 'Idempotency-Key': key } }),
  getCustomerExport: (jobId: string) => apiClient.get(`/admin/gdpr/export-jobs/${encodeURIComponent(jobId)}`),
  customerExportDownloadUrl: (jobId: string) => apiClient.getUri({ url: `/admin/gdpr/export-jobs/${encodeURIComponent(jobId)}/download` }),
};

// Werksreset (Admin, verlangt admin:system)
export const factoryResetApi = {
  /** Was wuerde verschwinden — echte Zahlen fuer den Warnhinweis. */
  getVorschau: () => apiClient.get('/admin/reset/preview'),
  starten: (daten: { password: string; mfaCode?: string; confirmation: string }) =>
    apiClient.post('/admin/reset/execute', daten),
  /** Ohne jobId liefert der Server den letzten Vorgang — so findet die Oberflaeche
   *  nach einem Neuladen wieder Anschluss. */
  getStatus: (jobId?: number) =>
    apiClient.get('/admin/reset/status', { params: jobId ? { jobId } : {} }),
};

// API Tokens API (Admin only)
export const apiTokensApi = {
  list: () => apiClient.get('/admin/api-tokens'),
  create: (data: {
    name: string;
    scopes?: string;
    customer_id?: number | string | null;
    /** Gueltigkeit in Tagen; null/undefined = unbegrenzt. */
    expires_in_days?: number | null;
    /** Anonymisierungsstufe fuer alles, was ueber diesen Token ausgeliefert wird. */
    pii_profile?: 'none' | 'normal' | 'standard' | 'strict';
  }) => apiClient.post('/admin/api-tokens', data),
  delete: (id: string | number) => apiClient.delete(`/admin/api-tokens/${id}`),
};

// Custom Live-Chat API (Admin)
export const liveChatApi = {
  listChats: () => apiClient.get('/chats/admin/chats'),
  getChatMessages: (id: string | number, lastId?: number) =>
    apiClient.get(`/chats/admin/chats/${id}/messages`, { params: { last_id: lastId } }),
  sendAgentMessage: (id: string | number, message: string) =>
    apiClient.post(`/chats/admin/chats/${id}/messages`, { message }),
  assignChat: (id: string | number, agentId?: number) =>
    apiClient.post(`/chats/admin/chats/${id}/assign`, { agent_id: agentId }),
  closeChat: (id: string | number) =>
    apiClient.post(`/chats/admin/chats/${id}/close`),
};

// AI Self-Service Workflows API (Admin)
export const aiWorkflowsApi = {
  listWorkflows: () => apiClient.get('/admin/ai-workflows'),
  createWorkflow: (data: { trigger_keywords: string; action_type: string; response_template: string; is_active?: number }) =>
    apiClient.post('/admin/ai-workflows', data),
  updateWorkflow: (id: number | string, data: any) =>
    apiClient.put(`/admin/ai-workflows/${id}`, data),
  deleteWorkflow: (id: number | string) =>
    apiClient.delete(`/admin/ai-workflows/${id}`),
  getOpenClawSettings: () =>
    apiClient.get('/admin/ai-workflows-settings/openclaw'),
  updateOpenClawSettings: (data: { enabled: boolean; url: string; key: string }) =>
    apiClient.put('/admin/ai-workflows-settings/openclaw', data),
};

// Internal Chat & Presence API
export const internalChatApi = {
  sendHeartbeat: (data: { current_path?: string; ticket_id?: number | null }) =>
    apiClient.post('/internal-chats/heartbeat', data),

  getActivePresence: () =>
    apiClient.get('/internal-chats/active'),

  getChats: () =>
    apiClient.get('/internal-chats'),

  getMessages: (agentId: number | string) =>
    apiClient.get(`/internal-chats/${agentId}/messages`),

  sendMessage: (agentId: number | string, message: string) =>
    apiClient.post(`/internal-chats/${agentId}/messages`, { message }),
};

// System Logs API (Admin only)
export const systemLogsApi = {
  getLogs: (params?: {
    level?: string;
    category?: string;
    search?: string;
    correlation_id?: string;
    from?: string;
    to?: string;
    page?: number;
    limit?: number;
  }) => apiClient.get('/admin/system-logs', { params }),

  exportLogs: () => apiClient.get('/admin/system-logs/export', { responseType: 'blob' }),

  deleteExportedLogs: (exportReceipt: string) =>
    apiClient.delete('/admin/system-logs', { data: { export_receipt: exportReceipt } }),
  
  logFrontendError: (data: {
    level?: 'info' | 'warn' | 'error';
    message: string;
    details?: string;
    correlation_id?: string;
    // Frueher /admin/system-logs/frontend — das lag hinter admin:system, ein Agent
    // oder Portalkunde konnte seinen eigenen Fehler also gar nicht melden.
  }) => apiClient.post('/client-errors', data),
};

// External Providers & Wartungsverträge APIs (Admin/Agent)
export const externalProvidersApi = {
  list: () => apiClient.get('/admin/external-providers'),
  
  create: (formData: FormData) => apiClient.post('/admin/external-providers', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  
  update: (id: number | string, formData: FormData) => apiClient.put(`/admin/external-providers/${id}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  
  delete: (id: number | string) => apiClient.delete(`/admin/external-providers/${id}`),
  
  downloadContractUrl: (id: number | string) => `/api/admin/external-providers/${id}/contract/download`,
  
  escalateTicket: (ticketId: number | string, data: { providerId: number; agentNote?: string | null; idempotencyKey?: string; disclosure: {fields: string[]; replyIds: number[]; attachmentIds: number[]} }) =>
    apiClient.post(`/admin/tickets/${ticketId}/escalate`, data, data.idempotencyKey ? {
      headers: { 'Idempotency-Key': data.idempotencyKey },
    } : undefined),
    
  getEscalation: (ticketId: number | string) =>
    apiClient.get(`/admin/tickets/${ticketId}/escalation`),

  retryEscalation: (ticketId: number | string, escalationId: number | string) =>
    apiClient.post(`/admin/tickets/${ticketId}/escalations/${escalationId}/retry`),

  cancelEscalation: (ticketId: number | string, escalationId: number | string) =>
    apiClient.post(`/admin/tickets/${ticketId}/escalations/${escalationId}/cancel`),

  acceptEscalationSolution: (ticketId: number | string, escalationId: number | string) =>
    apiClient.post(`/admin/tickets/${ticketId}/escalations/${escalationId}/accept-solution`),

  rejectEscalationSolution: (ticketId: number | string, escalationId: number | string) =>
    apiClient.post(`/admin/tickets/${ticketId}/escalations/${escalationId}/reject-solution`),

  switchEscalationProvider: (ticketId: number | string, escalationId: number | string, providerId: number | string, agentNote: string, disclosure: {fields:string[];replyIds:number[];attachmentIds:number[]}) => {
    const idempotencyKey = crypto.randomUUID();
    return apiClient.post(
      `/admin/tickets/${ticketId}/escalations/${escalationId}/switch-provider`,
      { providerId, agentNote, idempotencyKey, disclosure },
      { headers: { 'Idempotency-Key': idempotencyKey } },
    );
  },
};

// Public Vendor Escalation Portal APIs (Auth-free via secure token)
export const publicVendorEscalationApi = {
  getDetails: (token: string) => apiClient.get(`/public/vendor-escalation/${token}`),
  
  submitComment: (token: string, data: { comment: string; markAsResolved?: boolean }) =>
    apiClient.post(`/public/vendor-escalation/${token}/comment`, data),
};

// Compliance & Security Center API (Admin only)
export const complianceApi = {
  getStatus: () => apiClient.get('/admin/compliance/status'),
  downloadSummaryReport: () => apiClient.get('/admin/compliance/report', { responseType: 'blob' }),
  getPolicies: () => apiClient.get('/admin/compliance/policies'),
  updatePolicies: (data: {
    enforce_mfa: boolean;
    idle_timeout_minutes: number;
    max_concurrent_sessions: number;
    audit_hash_chain_enabled: boolean;
    ip_whitelist: string | null;
  }) => apiClient.put('/admin/compliance/policies', data),
  listIncidents: () => apiClient.get('/admin/compliance/incidents'),
  createIncident: (data: {
    ticket_id?: number | null;
    title: string;
    description: string;
    severity: 'low' | 'medium' | 'high' | 'critical';
    status?: 'identified' | 'analyzed' | 'contained' | 'resolved';
    detected_at: string;
  }) => apiClient.post('/admin/compliance/incidents', data),
  updateIncident: (id: number | string, status: 'identified' | 'analyzed' | 'contained' | 'resolved') =>
    apiClient.patch(`/admin/compliance/incidents/${id}`, { status }),
  downloadReport: (id: number | string, type: 'early_warning' | 'detailed') =>
    apiClient.get(`/admin/compliance/incidents/${id}/report`, { params: { type }, responseType: 'blob' }),
  verifyAuditLedger: () => apiClient.post('/admin/compliance/audit/verify'),
};

export default apiClient;
